import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, addDoc, getDocs, query, where, updateDoc, doc, Timestamp, deleteDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import Factura from '../components/Factura';
import EmailModal from '../components/EmailModal';
import ClienteForm from '../components/ClienteForm';
import Toast from '../components/Toast';
import PagoMultiple from '../components/PagoMultiple';
import DivisionCuenta from '../components/DivisionCuenta';
import { useReactToPrint } from 'react-to-print';
import { sendFacturaEmail } from '../utils/emailService';
import '../styles/mobile.css';

const DEFAULT_PROFILE_IMAGE = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png';

// Configuración para sistema de comisiones interlocales
const CONFIGURACION_COMISIONES = {
  'Local sede C': {
    pagaComisionA: ['Local sede F'],
    recibeComisionDe: ['Local sede F'],
    porcentajeComision: 0.10
  },
  'Local sede F': {
    pagaComisionA: ['Local sede C'],
    recibeComisionDe: ['Local sede C'],
    porcentajeComision: 0.10
  },
  'Local el recuerdo': {
    pagaComisionA: [],
    recibeComisionDe: [],
    porcentajeComision: 0
  }
};

const SUCURSAL_CATEGORIAS = ['Local sede C', 'Local sede F', 'Local el recuerdo'];

// Enhanced responsive utilities
const useResponsive = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    isMobile: windowSize.width < 768,
    isTablet: windowSize.width >= 768 && windowSize.width < 1024,
    isDesktop: windowSize.width >= 1024,
    isSmallMobile: windowSize.width < 480,
    width: windowSize.width,
    height: windowSize.height
  };
};

// Add viewport helper functions
const isMobile = () => window.innerWidth < 768;
const isTablet = () => window.innerWidth >= 768 && window.innerWidth < 1024;
const isSmallMobile = () => window.innerWidth < 480;

const getResponsiveSize = (mobileSize, tabletSize, desktopSize) => {
  if (isMobile()) return mobileSize;
  if (isTablet()) return tabletSize || desktopSize;
  return desktopSize;
};

const getResponsiveColumns = (type = 'default') => {
  switch (type) {
    case 'stats':
      if (isSmallMobile()) return '1fr';
      if (isMobile()) return 'repeat(2, 1fr)';
      if (isTablet()) return 'repeat(3, 1fr)';
      return 'repeat(3, 1fr)';
    
    case 'payment-methods':
      if (isSmallMobile()) return '1fr';
      if (isMobile()) return 'repeat(2, 1fr)';
      if (isTablet()) return 'repeat(3, 1fr)';
      return 'repeat(3, 1fr)';
    
    case 'products':
      if (isSmallMobile()) return 'repeat(2, 1fr)';
      if (isMobile()) return 'repeat(2, 1fr)';
      if (isTablet()) return 'repeat(3, 1fr)';
      return 'repeat(auto-fill, minmax(150px, 1fr))';
    
    case 'orders':
      if (isMobile()) return '1fr';
      return 'repeat(auto-fill, minmax(300px, 1fr))';
    
    default:
      if (isMobile()) return '1fr';
      if (isTablet()) return 'repeat(3, 1fr)';
      return 'repeat(auto-fill, minmax(150px, 1fr))';
  }
};

const getResponsivePadding = (small, medium, large) => {
  if (isMobile()) return small;
  if (isTablet()) return medium || large;
  return large;
};

const getResponsiveGap = (small, medium, large) => {
  if (isMobile()) return small;
  if (isTablet()) return medium || large;
  return large;
};

// Función para detectar y manejar ventas interlocales
const detectarVentaInterlocal = async (ventaData, sucursalVendedora, productos) => {
  try {
    console.log("🔍 DETECTAR VENTA INTERLOCAL - Inicio:", {
      sucursalVendedora,
      productosEnVenta: ventaData.productos.length
    });
    
    const productosInterlocales = [];
    let totalComision = 0;

    // Revisar cada producto vendido
    for (const productoVenta of ventaData.productos) {
      const producto = productos.find(p => p.id === productoVenta.id);
      
      console.log(`🔎 Analizando producto ${productoVenta.nombre}:`, {
        id: productoVenta.id,
        sucursalOrigen: producto?.sucursalOrigen,
        sucursalVendedora: sucursalVendedora
      });
      
      if (!producto || !producto.sucursalOrigen) {
        console.log("⚠️ Producto sin sucursal origen, saltando...");
        continue;
      }

      const sucursalOrigen = producto.sucursalOrigen;
      const configuracion = CONFIGURACION_COMISIONES[sucursalVendedora];

      console.log("📋 Configuración para", sucursalVendedora, ":", configuracion);

      // Verificar si esta sucursal debe pagar comisión por vender productos de otra sucursal
      if (configuracion && 
          sucursalOrigen !== sucursalVendedora && 
          configuracion.pagaComisionA.includes(sucursalOrigen)) {
        
        const subtotalProducto = productoVenta.precio * productoVenta.cantidadVenta;
        const comisionProducto = subtotalProducto * configuracion.porcentajeComision;

        console.log("💰 COMISIÓN DETECTADA:", {
          producto: productoVenta.nombre,
          subtotal: subtotalProducto,
          comision: comisionProducto,
          porcentaje: configuracion.porcentajeComision
        });

        productosInterlocales.push({
          productoId: productoVenta.id,
          productoNombre: productoVenta.nombre,
          cantidad: productoVenta.cantidadVenta,
          precio: productoVenta.precio,
          subtotal: subtotalProducto,
          comision: comisionProducto,
          sucursalOrigen: sucursalOrigen,
          sucursalVendedora: sucursalVendedora
        });

        totalComision += comisionProducto;
      } else {
        console.log("❌ Sin comisión para este producto:", {
          razon: !configuracion ? "Sin configuración" : 
                 sucursalOrigen === sucursalVendedora ? "Misma sucursal" :
                 !configuracion.pagaComisionA.includes(sucursalOrigen) ? "No en lista de comisiones" : "Desconocida"
        });
      }
    }

    // Si hay productos interlocales, registrar la transacción de comisión
    if (productosInterlocales.length > 0) {
      const transaccionComision = {
        ventaId: null, // Se asignará después de crear la venta
        sucursalVendedora: sucursalVendedora,
        sucursalAcreedora: productosInterlocales[0].sucursalOrigen, // Asumiendo una sucursal acreedora por venta
        productos: productosInterlocales,
        totalComision: totalComision,
        porcentajeComision: CONFIGURACION_COMISIONES[sucursalVendedora].porcentajeComision,
        fecha: Timestamp.now(),
        estado: 'pendiente', // pendiente, pagada
        fechaPago: null
      };

      console.log("✅ TRANSACCIÓN DE COMISIÓN CREADA:", transaccionComision);

      return {
        esVentaInterlocal: true,
        transaccionComision,
        totalComision
      };
    }

    console.log("❌ No se detectaron ventas interlócales");
    return {
      esVentaInterlocal: false,
      transaccionComision: null,
      totalComision: 0
    };

  } catch (error) {
    console.error('❌ Error al detectar venta interlocal:', error);
    return {
      esVentaInterlocal: false,
      transaccionComision: null,
      totalComision: 0
    };
  }
};

function DashboardVendedor() {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState({
    nombre: '',
    rol: '',
    imagenURL: DEFAULT_PROFILE_IMAGE
  });
  const [productos, setProductos] = useState([]);
  const [ventaActual, setVentaActual] = useState({
    productos: [],
    total: 0,
    efectivo: '',
    cambio: 0,
    metodoPago: 'efectivo', // Add payment method
    mesa: null, // Agregar mesa seleccionada
  });
  
  // Estado para manejar múltiples métodos de pago
  const [pagoMultiple, setPagoMultiple] = useState({
    activo: false,
    pagos: [],
    totalPagado: 0,
    restante: 0
  });
  
  // Estado para mesas disponibles
  const [mesas, setMesas] = useState([]);
  const [historialVentas, setHistorialVentas] = useState([]);
  const [seccionActiva, setSeccionActiva] = useState('panel');
  const [ventasDelDia, setVentasDelDia] = useState([]);
  const [totalDelDia, setTotalDelDia] = useState(0);
  const [topProductos, setTopProductos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [menuMovilAbierto, setMenuMovilAbierto] = useState(false);
  const [ventaParaFacturar, setVentaParaFacturar] = useState(null);
  const [mostrarPreviewFactura, setMostrarPreviewFactura] = useState(false);  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showClienteForm, setShowClienteForm] = useState(false);
  const [clienteData, setClienteData] = useState(null);
  const [ventasPendientes, setVentasPendientes] = useState([]);
  const [filtroCategoria, setFiltroCategoria] = useState('Todas');
  const [categorias, setCategorias] = useState([]); // Categorías dinámicas desde Firestore
  const [metodoPagoStats, setMetodoPagoStats] = useState({
    efectivo: 0,
    tarjeta: 0,
    transferencia: 0
  });
  const facturaRef = useRef();
  const ordenRef = useRef();
  const [ordenesEnProceso, setOrdenesEnProceso] = useState([]);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
  const [mostrarPreviewOrden, setMostrarPreviewOrden] = useState(false);
  const [mostrarPagoMultiple, setMostrarPagoMultiple] = useState(false);

  // Estados para división de cuentas
  const [mostrarDivisionCuenta, setMostrarDivisionCuenta] = useState(false);
  const [cuentasDivididas, setCuentasDivididas] = useState([]);
  const [procesandoCuentas, setProcesandoCuentas] = useState(false);
  const [cuentaEnProceso, setCuentaEnProceso] = useState(null); // Nueva cuenta siendo procesada
  const [cuentasProcesadas, setCuentasProcesadas] = useState([]); // Cuentas ya procesadas
  const [procesandoVenta, setProcesandoVenta] = useState(false); // Para evitar doble clic

  // Estado para rastrear órdenes ya impresas
  const [ordenesImpresas, setOrdenesImpresas] = useState(() => {
    // Cargar estado desde localStorage al inicializar
    const stored = localStorage.getItem('ordenesImpresas');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  // Estado para los mensajes Toast
  const [toast, setToast] = useState({
    visible: false,
    message: '',
    type: 'success'
  });

  // Función para mostrar mensajes
  const showToast = (message, type = 'success') => {
    setToast({
      visible: true,
      message,
      type
    });
  };

  // Función para cerrar el toast
  const closeToast = () => {
    setToast({ ...toast, visible: false });
  };

  // Función para cargar categorías desde Firestore
  const cargarCategorias = async () => {
    try {
      const categoriasSnapshot = await getDocs(collection(db, "categorias"));
      const categoriasDatos = categoriasSnapshot.docs.map(doc => ({
        id: doc.id,
        nombre: doc.data().nombre
      }));
      
      setCategorias(categoriasDatos);
    } catch (error) {
      console.error("Error al cargar categorías:", error);
      // En caso de error, usar categorías básicas de respaldo
      setCategorias([
        { id: 'bebidas', nombre: 'Bebidas' },
        { id: 'postres', nombre: 'Postres' },
        { id: 'helados', nombre: 'Helados' }
      ]);
    }
  };

  // Función reutilizable para cargar ventas por sucursal
  const cargarVentasPorSucursal = async (sucursal) => {
    console.log("🔍 INICIO cargarVentasPorSucursal - Sucursal recibida:", sucursal);
    
    if (!sucursal || sucursal === 'No asignada') {
      console.log("❌ No se puede cargar ventas, sucursal no válida:", sucursal);
      // Limpiar datos si no hay sucursal válida
      setHistorialVentas([]);
      setVentasDelDia([]);
      setTotalDelDia(0);
      setTopProductos([]);
      setMetodoPagoStats({
        efectivo: 0,
        tarjeta: 0,
        transferencia: 0
      });
      return;
    }
    
    try {
      console.log("🔍 Cargando ventas para sucursal:", sucursal);
      console.log("📅 Timestamp actual:", new Date().toISOString());
      
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const finDelDia = new Date(hoy);
      finDelDia.setHours(23, 59, 59, 999);
      
      console.log("📅 Rango de fechas:", { 
        inicio: hoy.toISOString(), 
        fin: finDelDia.toISOString(),
        inicioTimestamp: Timestamp.fromDate(hoy),
        finTimestamp: Timestamp.fromDate(finDelDia)
      });
      
      // Primero, verificar todas las ventas sin filtro de sucursal para debug
      const todasVentasQuery = query(
        collection(db, "ventas"),
        where("fecha", ">=", Timestamp.fromDate(hoy)),
        where("fecha", "<=", Timestamp.fromDate(finDelDia))
      );
      
      console.log("🔎 Ejecutando query para todas las ventas...");
      const todasVentasSnapshot = await getDocs(todasVentasQuery);
      const todasVentas = todasVentasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha.toDate()
      }));
      
      console.log("🔎 Todas las ventas del día encontradas:", todasVentas.length);
      console.log("🔎 Detalle de todas las ventas:", todasVentas);
      console.log("🏢 Sucursales encontradas en ventas:", [...new Set(todasVentas.map(v => v.sucursal))]);
      
      // Ahora filtrar por sucursal
      console.log("🔍 Aplicando filtro por sucursal:", sucursal);
      const ventasQuery = query(
        collection(db, "ventas"),
        where("fecha", ">=", Timestamp.fromDate(hoy)),
        where("fecha", "<=", Timestamp.fromDate(finDelDia))
        // Comentado temporalmente para evitar error de índice
        // where("sucursal", "==", sucursal)
      );

      console.log("🔎 Ejecutando query por fecha únicamente...");
      const ventasSnapshot = await getDocs(ventasQuery);
      const todasVentasDelDia = ventasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha.toDate() // Convertir timestamp a Date
      }));

      // Filtrar por sucursal en JavaScript
      console.log("🔍 Filtrando por sucursal en JavaScript (temporal)...");
      const ventasData = todasVentasDelDia.filter(venta => venta.sucursal === sucursal);

      console.log("✅ RESULTADO: Ventas filtradas para sucursal '" + sucursal + "':", ventasData.length);
      console.log("� Detalle de ventas filtradas:", ventasData);
      const totalCalculado = ventasData.reduce((sum, v) => sum + v.total, 0);
      console.log("💰 Total calculado:", totalCalculado);
      
      // Actualizar estados uno por uno con logs
      console.log("🔄 Actualizando historialVentas...");
      setHistorialVentas(ventasData);
      
      console.log("🔄 Actualizando ventasDelDia...");
      setVentasDelDia(ventasData);
      
      console.log("🔄 Actualizando totalDelDia...");
      setTotalDelDia(totalCalculado);
      
      console.log("🔄 Calculando top productos...");
      calcularTopProductos(ventasData);
      
      console.log("🔄 Calculando estadísticas por método de pago...");
      calcularEstadisticasPorMetodoPago(ventasData);

      console.log("🔄 Cargando ventas pendientes...");
      await cargarVentasPendientes();
      
      console.log("🔄 Cargando órdenes en proceso...");
      await cargarOrdenesEnProceso();
      
      console.log("✅ COMPLETADO: cargarVentasPorSucursal finalizado exitosamente");
    } catch (error) {
      console.error("❌ ERROR en cargarVentasPorSucursal:", error);
      console.error("❌ Stack trace:", error.stack);
      // En caso de error, limpiar los datos
      setHistorialVentas([]);
      setVentasDelDia([]);
      setTotalDelDia(0);
      setTopProductos([]);
      setMetodoPagoStats({
        efectivo: 0,
        tarjeta: 0,
        transferencia: 0
      });
    }
  };

  // Cargar perfil de usuario
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        console.log("🔄 Iniciando carga de datos del vendedor...");
        const userData = JSON.parse(localStorage.getItem('user'));
        if (!userData) {
          console.log("❌ No hay datos de usuario, redirigiendo al login");
          navigate('/');
          return;
        }

        console.log("👤 Datos de usuario encontrados:", userData);

        // Cargar perfil del vendedor
        const vendedorQuery = query(
          collection(db, "vendedores"), 
          where("username", "==", userData.username)
        );
        const vendedorSnapshot = await getDocs(vendedorQuery);
        
        if (!vendedorSnapshot.empty) {
          const vendedorData = vendedorSnapshot.docs[0].data();
          const validatedImageUrl = await validateAndLoadImage(vendedorData.imagenURL);
          
          const userProfileData = {
            nombre: vendedorData.username,
            rol: vendedorData.rol,
            imagenURL: validatedImageUrl,
            sucursal: vendedorData.sucursal || 'No asignada'
          };
          
          console.log("✅ Perfil de usuario cargado:", userProfileData);
          console.log("🏢 Sucursal asignada:", userProfileData.sucursal);
          
          // Establecer el perfil primero
          setUserProfile(userProfileData);
          
          // Cargar ventas INMEDIATAMENTE después de establecer el perfil
          console.log("🔄 Cargando ventas inmediatamente para sucursal:", userProfileData.sucursal);
          if (userProfileData.sucursal && userProfileData.sucursal !== 'No asignada') {
            await cargarVentasPorSucursal(userProfileData.sucursal);
            // Cargar mesas disponibles para esta sucursal
            await cargarMesas(userProfileData.sucursal);
          } else {
            console.log("⚠️ No se puede cargar ventas: sucursal no válida");
          }
        } else {
          console.log("❌ No se encontró información del vendedor");
        }

        // Cargar productos del inventario
        console.log("📦 Cargando productos...");
        const productosSnapshot = await getDocs(collection(db, "productos"));
        const productosData = productosSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          cantidadVenta: 0, // Para control de cantidad en la venta
          categoria: doc.data().categoria || 'Sin categoría' // Asegurar que todos los productos tengan categoría
        }));
        setProductos(productosData);
        console.log("✅ Productos cargados:", productosData.length);
      } catch (error) {
        console.error("❌ Error detallado al cargar datos:", error);
      }
    };
    cargarDatos();
  }, [navigate]);

  // useEffect para cargar categorías
  useEffect(() => {
    cargarCategorias();
  }, []);

  // useEffect para monitorear cambios en los estados de ventas (debugging)
  useEffect(() => {
    console.log("📊 MONITOR - Cambio en ventasDelDia:", {
      cantidad: ventasDelDia.length,
      total: totalDelDia,
      timestamp: new Date().toISOString()
    });
  }, [ventasDelDia, totalDelDia]);

  useEffect(() => {
    console.log("👤 MONITOR - Cambio en userProfile:", {
      nombre: userProfile.nombre,
      sucursal: userProfile.sucursal,
      timestamp: new Date().toISOString()
    });
  }, [userProfile]);

  // Efecto para monitorear cambios en ventaActual
  useEffect(() => {
    console.log("🛒 MONITOR - Cambio en ventaActual:", {
      cantidadProductos: ventaActual.productos.length,
      productos: ventaActual.productos.map(p => ({ nombre: p.nombre, cantidad: p.cantidadVenta })),
      total: ventaActual.total,
      timestamp: new Date().toISOString()
    });
  }, [ventaActual]);

  // Efecto para guardar órdenes impresas en localStorage
  useEffect(() => {
    localStorage.setItem('ordenesImpresas', JSON.stringify([...ordenesImpresas]));
  }, [ordenesImpresas]);

  // Función para cargar mesas de la sucursal actual
  const cargarMesas = async (sucursalActual) => {
    try {
      if (!sucursalActual || sucursalActual === 'No asignada') {
        console.log("⚠️ No se puede cargar mesas: sucursal no válida");
        setMesas([]);
        return;
      }

      console.log("🔍 Cargando mesas para sucursal:", sucursalActual);
      
      const mesasQuery = query(
        collection(db, "mesas"),
        where("sucursal", "==", sucursalActual)
      );
      const mesasSnapshot = await getDocs(mesasQuery);
      
      const mesasDB = mesasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log("✅ Mesas cargadas:", mesasDB);
      setMesas(mesasDB);
    } catch (error) {
      console.error("❌ Error al cargar mesas:", error);
      showToast("Error al cargar mesas", 'error');
    }
  };

  const agregarProductoVenta = (producto) => {
    console.log("🛒 AGREGANDO PRODUCTO:", producto.nombre, "ID:", producto.id);
    console.log("📦 Stock disponible:", producto.cantidad);
    
    // Buscar si el producto ya está en el carrito
    const productoEnCarrito = ventaActual.productos.find(p => p.id === producto.id);
    const cantidadActualEnCarrito = productoEnCarrito ? productoEnCarrito.cantidadVenta : 0;
    
    console.log("🔢 Cantidad actual en carrito:", cantidadActualEnCarrito);
    
    // Permitir vender productos con stock undefined o null (sin límite)
    // Validar stock solo si el producto tiene cantidad definida (no es null o undefined)
    if (producto.cantidad !== null && producto.cantidad !== undefined && cantidadActualEnCarrito >= producto.cantidad) {
      console.log("❌ No hay suficiente stock");
      showToast('No hay suficiente stock', 'warning');
      return;
    }

    const nuevosProductos = productoEnCarrito
      ? ventaActual.productos.map(p => 
          p.id === producto.id 
            ? { ...p, cantidadVenta: p.cantidadVenta + 1 }
            : p
        )
      : [...ventaActual.productos, { ...producto, cantidadVenta: 1 }];

    const nuevoTotal = nuevosProductos.reduce(
      (sum, p) => sum + (p.precio * p.cantidadVenta), 0
    );

    console.log("🛒 Productos después de agregar:", nuevosProductos);
    console.log("💰 Nuevo total:", nuevoTotal);

    setVentaActual({
      ...ventaActual,
      productos: nuevosProductos,
      total: nuevoTotal,
      cambio: ventaActual.efectivo ? ventaActual.efectivo - nuevoTotal : 0
    });
    
    console.log("✅ Estado actualizado");
    showToast(`${producto.nombre} agregado al carrito`, 'success');
  };

  const quitarProductoVenta = (productoId) => {
    const nuevosProductos = ventaActual.productos.filter(p => p.id !== productoId);
    const nuevoTotal = nuevosProductos.reduce(
      (sum, p) => sum + (p.precio * p.cantidadVenta), 0
    );

    setVentaActual({
      ...ventaActual,
      productos: nuevosProductos,
      total: nuevoTotal,
      cambio: ventaActual.efectivo ? ventaActual.efectivo - nuevoTotal : 0
    });
    
    // Recalcular pago múltiple si está activo
    if (pagoMultiple.activo) {
      const restante = nuevoTotal - pagoMultiple.totalPagado;
      setPagoMultiple({
        ...pagoMultiple,
        restante: Math.max(0, restante)
      });
    }
  };

  // Función para cambiar la mesa seleccionada
  const cambiarMesaSeleccionada = (mesaId) => {
    // Buscar la mesa seleccionada por ID
    const mesaSeleccionada = mesas.find(m => m.id === mesaId);
    
    if (mesaSeleccionada) {
      setVentaActual({
        ...ventaActual,
        mesa: mesaSeleccionada
      });
      showToast(`Mesa ${mesaSeleccionada.numero} seleccionada`, 'info');
    }
  };

  // Funciones para manejar múltiples métodos de pago
  const activarPagoMultiple = () => {
    setPagoMultiple({
      activo: true,
      pagos: [],
      totalPagado: 0,
      restante: ventaActual.total
    });
  };

  const desactivarPagoMultiple = () => {
    setPagoMultiple({
      activo: false,
      pagos: [],
      totalPagado: 0,
      restante: 0
    });
    // Limpiar campos de pago único
    setVentaActual({
      ...ventaActual,
      metodoPago: 'efectivo',
      efectivo: '',
      cambio: 0
    });
  };

  const agregarPago = (metodoPago, monto, efectivoRecibido = 0) => {
    const montoNumerico = parseFloat(monto);
    
    if (!montoNumerico || montoNumerico <= 0) {
      showToast('Ingrese un monto válido', 'warning');
      return;
    }

    if (montoNumerico > pagoMultiple.restante) {
      showToast('El monto no puede ser mayor al restante por pagar', 'warning');
      return;
    }

    if (metodoPago === 'efectivo' && efectivoRecibido < montoNumerico) {
      showToast('El efectivo recibido debe ser mayor o igual al monto', 'warning');
      return;
    }

    const nuevoPago = {
      id: Date.now(),
      metodoPago,
      monto: montoNumerico,
      efectivoRecibido: metodoPago === 'efectivo' ? parseFloat(efectivoRecibido) : montoNumerico,
      cambio: metodoPago === 'efectivo' ? parseFloat(efectivoRecibido) - montoNumerico : 0
    };

    const nuevosPagos = [...pagoMultiple.pagos, nuevoPago];
    const nuevoTotalPagado = pagoMultiple.totalPagado + montoNumerico;
    const nuevoRestante = ventaActual.total - nuevoTotalPagado;

    setPagoMultiple({
      ...pagoMultiple,
      pagos: nuevosPagos,
      totalPagado: nuevoTotalPagado,
      restante: Math.max(0, nuevoRestante)
    });
  };

  const eliminarPago = (pagoId) => {
    const pagoAEliminar = pagoMultiple.pagos.find(p => p.id === pagoId);
    const nuevosPagos = pagoMultiple.pagos.filter(p => p.id !== pagoId);
    const nuevoTotalPagado = pagoMultiple.totalPagado - pagoAEliminar.monto;
    const nuevoRestante = ventaActual.total - nuevoTotalPagado;

    setPagoMultiple({
      ...pagoMultiple,
      pagos: nuevosPagos,
      totalPagado: nuevoTotalPagado,
      restante: Math.max(0, nuevoRestante)
    });
  };

  // Funciones para división de cuentas
  const iniciarDivisionCuenta = () => {
    if (!ventaActual.productos.length) {
      showToast('No hay productos para dividir', 'warning');
      return;
    }
    setMostrarDivisionCuenta(true);
  };

  const confirmarDivisionCuenta = (cuentas) => {
    setCuentasDivididas(cuentas);
    setMostrarDivisionCuenta(false);
    setProcesandoCuentas(true);
    
    // Limpiar la venta actual ya que ahora procesaremos las cuentas individuales
    setVentaActual({
      productos: [],
      total: 0,
      efectivo: '',
      cambio: 0,
      metodoPago: 'efectivo'
    });

    showToast(`División completada: ${cuentas.length} cuentas creadas`, 'success');
  };

  const procesarCuentaIndividual = async (cuenta) => {
    try {
      // Configurar la venta actual con los datos de la cuenta
      const ventaCuenta = {
        productos: cuenta.productos.map(p => ({
          ...p,
          cantidadVenta: p.cantidadAsignada
        })),
        total: cuenta.total,
        efectivo: '',
        cambio: 0,
        metodoPago: 'efectivo'
      };
      
      setVentaActual(ventaCuenta);
      setCuentaEnProceso(cuenta);
      
      // Limpiar estados de pago múltiple si estaban activos
      setPagoMultiple({
        activo: false,
        pagos: [],
        totalPagado: 0,
        restante: 0
      });
      
      showToast(`Configurando pago para ${cuenta.nombre} - Total: $${formatearNumero(cuenta.total)}`, 'info');
    } catch (error) {
      console.error('Error al procesar cuenta individual:', error);
      showToast('Error al procesar la cuenta', 'error');
    }
  };

  const cancelarDivisionCuentas = () => {
    setCuentasDivididas([]);
    setProcesandoCuentas(false);
    setCuentaEnProceso(null);
    setCuentasProcesadas([]);
    
    // Limpiar venta actual si está relacionada con cuentas divididas
    setVentaActual({
      productos: [],
      total: 0,
      efectivo: '',
      cambio: 0,
      metodoPago: 'efectivo',
      mesa: null // Limpiar mesa seleccionada
    });
    
    showToast('División de cuentas cancelada', 'info');
  };

  // Modificar la función de registro de venta
  const registrarVenta = async () => {
    // Protección contra doble clic
    if (procesandoVenta) {
      showToast('Venta en proceso, por favor espere...', 'warning');
      return;
    }

    // Validaciones previas (antes de activar el estado de procesamiento)
    if (!ventaActual.productos.length) {
      showToast('Por favor, agregue productos a la venta', 'warning');
      return;
    }

    // Validaciones para pago múltiple
    if (pagoMultiple.activo) {
      if (pagoMultiple.restante > 0) {
        showToast('Falta pagar $' + formatearNumero(pagoMultiple.restante), 'warning');
        return;
      }
      if (pagoMultiple.pagos.length === 0) {
        showToast('Debe agregar al menos un método de pago', 'warning');
        return;
      }
    } else {
      // Validaciones para pago único
      if (ventaActual.metodoPago === 'efectivo' && 
          (!ventaActual.efectivo || ventaActual.efectivo < ventaActual.total)) {
        showToast('El efectivo recibido debe ser mayor o igual al total', 'error');
        return;
      }
    }

    // Activar estado de procesamiento solo después de pasar todas las validaciones
    setProcesandoVenta(true);

    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      
      let ventaData;
      
      if (pagoMultiple.activo) {
        // Preparar datos para venta con múltiples métodos de pago
        const totalEfectivoRecibido = pagoMultiple.pagos
          .filter(p => p.metodoPago === 'efectivo')
          .reduce((sum, p) => sum + p.efectivoRecibido, 0);
        
        const totalCambio = pagoMultiple.pagos
          .filter(p => p.metodoPago === 'efectivo')
          .reduce((sum, p) => sum + p.cambio, 0);

        ventaData = {
          productos: ventaActual.productos,
          total: ventaActual.total,
          efectivo: totalEfectivoRecibido,
          cambio: totalCambio,
          metodoPago: 'multiple', // Nuevo tipo de pago
          metodosPago: pagoMultiple.pagos, // Array con todos los métodos
          fecha: Timestamp.now(),
          vendedorId: userData.id,
          vendedorNombre: userData.username,
          sucursal: userProfile.sucursal || 'No asignada',
          mesa: ventaActual.mesa ? ventaActual.mesa.numero : null, // Agregar número de mesa si está seleccionada
          ordenId: ventaActual.ordenId // Preservar el ID de la orden original si existe
        };
      } else {
        // Venta con un solo método de pago (comportamiento original)
        ventaData = {
          productos: ventaActual.productos,
          total: ventaActual.total,
          efectivo: parseFloat(ventaActual.efectivo || 0),
          cambio: ventaActual.cambio,
          metodoPago: ventaActual.metodoPago,
          fecha: Timestamp.now(),
          vendedorId: userData.id,
          vendedorNombre: userData.username,
          sucursal: userProfile.sucursal || 'No asignada',
          mesa: ventaActual.mesa ? ventaActual.mesa.numero : null, // Agregar número de mesa si está seleccionada
          ordenId: ventaActual.ordenId // Preservar el ID de la orden original si existe
        };
      }

      // Si es una cuenta dividida, agregar información adicional
      if (cuentaEnProceso) {
        ventaData.esCuentaDividida = true;
        ventaData.nombreCuenta = cuentaEnProceso.nombre;
      }

      // Detectar si es una venta interlocal y calcular comisiones
      const sucursalVendedora = userProfile.sucursal || 'No asignada';
      console.log("🏪 Procesando venta para sucursal:", sucursalVendedora);
      
      const resultadoInterlocal = await detectarVentaInterlocal(ventaData, sucursalVendedora, productos);
      
      if (resultadoInterlocal.esVentaInterlocal) {
        console.log("🎯 VENTA INTERLOCAL DETECTADA - Agregando metadatos a la venta");
        ventaData.esVentaInterlocal = true;
        ventaData.totalComision = resultadoInterlocal.totalComision;
        ventaData.sucursalAcreedora = resultadoInterlocal.transaccionComision.sucursalAcreedora;
      }

      // Registrar venta
      console.log("💾 Guardando venta en Firebase...");
      const docRef = await addDoc(collection(db, "ventas"), ventaData);
      console.log("✅ Venta guardada con ID:", docRef.id);
      
      // Si es venta interlocal, registrar también en la colección de comisiones
      if (resultadoInterlocal.esVentaInterlocal) {
        console.log("💰 Guardando transacción de comisión...");
        const transaccionComision = {
          ...resultadoInterlocal.transaccionComision,
          ventaId: docRef.id
        };
        
        const comisionDoc = await addDoc(collection(db, "ventas_interlocales"), transaccionComision);
        console.log("✅ Comisión guardada con ID:", comisionDoc.id, transaccionComision);
      }
      
      // Set up invoice data
      setVentaParaFacturar({
        ...ventaData,
        id: docRef.id,
        fecha: new Date().toLocaleString()
      });
      setMostrarPreviewFactura(true);

      // Actualizar inventario
      for (const producto of ventaActual.productos) {
        try {
          // Solo actualizar el stock cuando está definido (no es null o undefined)
          if (producto.cantidad !== null && producto.cantidad !== undefined) {
            // Verificar si el producto todavía existe
            const productoRef = doc(db, "productos", producto.id);
            await updateDoc(productoRef, {
              cantidad: producto.cantidad - producto.cantidadVenta
            });
          } else {
            console.log("📦 Producto sin stock definido, no se actualiza inventario:", producto.nombre);
          }
        } catch (error) {
          console.error(`Error al actualizar el inventario para producto ${producto.nombre} (${producto.id}):`, error.message);
          // Continuar con los demás productos en caso de error
        }
      }

      // Si es una cuenta dividida, manejar el flujo especial
      if (cuentaEnProceso) {
        // Marcar esta cuenta como procesada
        setCuentasProcesadas([...cuentasProcesadas, cuentaEnProceso.id]);
        
        // Limpiar la cuenta en proceso
        setCuentaEnProceso(null);
        
        // Verificar si quedan cuentas por procesar
        const cuentasRestantes = cuentasDivididas.filter(
          cuenta => !cuentasProcesadas.includes(cuenta.id) && cuenta.id !== cuentaEnProceso.id
        );
        
        if (cuentasRestantes.length === 0) {
          // Todas las cuentas han sido procesadas
          setCuentasDivididas([]);
          setProcesandoCuentas(false);
          setCuentasProcesadas([]);
          showToast('Todas las cuentas han sido procesadas exitosamente', 'success');
        } else {
          showToast(`Cuenta procesada. Quedan ${cuentasRestantes.length} cuentas por procesar`, 'info');
        }
      } else {
        // Flujo normal (no es cuenta dividida)
        // Actualizar estado local
        setHistorialVentas([...historialVentas, { ...ventaData, id: docRef.id }]);
        setVentasDelDia([...ventasDelDia, ventaData]);
        setTotalDelDia(totalDelDia + ventaData.total);
        showToast('Venta registrada exitosamente', 'success');
      }

      // Limpiar venta actual
      setVentaActual({
        productos: [],
        total: 0,
        efectivo: '',
        cambio: 0,
        metodoPago: 'efectivo',
        mesa: null // Limpiar mesa seleccionada
      });
      
      // Limpiar pago múltiple
      setPagoMultiple({
        activo: false,
        pagos: [],
        totalPagado: 0,
        restante: 0
      });
      
      // Si la venta proviene de una orden de mesa, eliminar la orden original
      if (ventaActual.mesa && ventaActual.ordenId) {
        try {
          // Eliminar la orden de la colección "ordenes"
          await deleteDoc(doc(db, "ordenes", ventaActual.ordenId));
          console.log(`✅ Orden de mesa ${ventaActual.mesa.numero} eliminada tras cobro exitoso`);
          
          // Recargar órdenes en proceso para actualizar la interfaz
          await cargarOrdenesEnProceso();
        } catch (error) {
          console.error("Error al eliminar la orden original:", error);
        }
      }

      // Recargar datos actualizados
      await cargarVentasPorSucursal(userProfile.sucursal);
      
      // Recargar productos actualizados
      const productosSnapshot = await getDocs(collection(db, "productos"));
      const productosData = productosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        cantidadVenta: 0,
        categoria: doc.data().categoria || 'Sin categoría'
      }));
      setProductos(productosData);

    } catch (error) {
      console.error("Error al registrar venta:", error);
      showToast(`Error al registrar la venta: ${error.message}`, 'error');
    } finally {
      // Siempre desactivar el estado de procesamiento
      // Agregar un pequeño delay para evitar clics muy rápidos sucesivos
      setTimeout(() => {
        setProcesandoVenta(false);
      }, 1000);
    }
  };

  const calcularCambio = (efectivo) => {
    // Si efectivo es vacío, establecemos efectivo como vacío y cambio como 0
    if (efectivo === '') {
      setVentaActual({
        ...ventaActual,
        efectivo: '',
        cambio: 0
      });
      return;
    }
    
    // Asegurarse de que efectivo sea un número
    const efectivoNum = parseFloat(efectivo);
    if (isNaN(efectivoNum)) {
      return;
    }
    
    const cambio = efectivoNum - ventaActual.total;
    setVentaActual({
      ...ventaActual,
      efectivo: efectivoNum,
      cambio: cambio >= 0 ? cambio : 0
    });
  };

  // Función auxiliar para formatear fechas
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    if (fecha instanceof Timestamp) {
      return fecha.toDate().toLocaleString();
    }
    if (fecha instanceof Date) {
      return fecha.toLocaleString();
    }
    return new Date(fecha).toLocaleString();
  };

  // Modificar esta función para ordenar por cantidad vendida, no por valor
  const calcularTopProductos = (ventas) => {
    const productosVendidos = {};
    
    ventas.forEach(venta => {
      venta.productos.forEach(prod => {
        if (productosVendidos[prod.id]) {
          productosVendidos[prod.id].cantidadTotal += prod.cantidadVenta;
          productosVendidos[prod.id].ventasTotal += (prod.precio * prod.cantidadVenta);
        } else {
          productosVendidos[prod.id] = {
            id: prod.id,
            nombre: prod.nombre,
            cantidadTotal: prod.cantidadVenta,
            ventasTotal: prod.precio * prod.cantidadVenta,
            imagenURL: prod.imagenURL
          };
        }
      });
    });

    // Ordenar por cantidadTotal en lugar de ventasTotal
    const topProductosArray = Object.values(productosVendidos)
      .sort((a, b) => b.cantidadTotal - a.cantidadTotal)
      .slice(0, 3);

    setTopProductos(topProductosArray);
  };

  // Añadir función para calcular estadísticas por método de pago
  const calcularEstadisticasPorMetodoPago = (ventas) => {
    const stats = {
      efectivo: 0,
      tarjeta: 0,
      transferencia: 0
    };
    
    ventas.forEach(venta => {
      if (venta.metodoPago === 'multiple' && venta.metodosPago) {
        // Manejar ventas con múltiples métodos de pago
        venta.metodosPago.forEach(pago => {
          const metodoPago = pago.metodoPago ? pago.metodoPago.toLowerCase() : 'efectivo';
          if (metodoPago === 'efectivo') {
            stats.efectivo += pago.monto;
          } else if (metodoPago === 'tarjeta') {
            stats.tarjeta += pago.monto;
          } else if (metodoPago === 'transferencia') {
            stats.transferencia += pago.monto;
          } else {
            // Cualquier otro método (nequi, daviplata, etc.) se considera como efectivo
            stats.efectivo += pago.monto;
          }
        });
      } else {
        // Manejar ventas con un solo método de pago (comportamiento original)
        const metodoPago = venta.metodoPago ? venta.metodoPago.toLowerCase() : 'efectivo';
        
        if (metodoPago === 'efectivo') {
          stats.efectivo += venta.total;
        } else if (metodoPago === 'tarjeta') {
          stats.tarjeta += venta.total;
        } else if (metodoPago === 'transferencia') {
          stats.transferencia += venta.total;
        } else {
          // Cualquier otro método (nequi, daviplata, etc.) se considera como efectivo
          stats.efectivo += venta.total;
        }
      }
    });
    
    setMetodoPagoStats(stats);
  };

  const formatearNumero = (numero) => {
    return new Intl.NumberFormat('es-CO', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(numero);
  };

  const renderSeccion = () => {
    switch(seccionActiva) {
      case 'panel':
        return (
          <div className={isMobile() ? 'vendedor-content' : ''} style={{ 
            padding: getResponsivePadding('4px', '12px', '16px'),
            fontSize: getResponsiveSize('14px', '15px', '16px'),
            paddingTop: isMobile() ? '5px' : getResponsivePadding('8px', '12px', '16px')
          }}>
            <h2 className={isMobile() ? 'vendedor-section-title vendedor-panel-principal' : ''} style={{ 
              fontSize: getResponsiveSize('20px', '22px', '24px'),
              marginBottom: getResponsiveSize('15px', '18px', '20px'),
              textAlign: isMobile() ? 'center' : 'left'
            }}>
              📊 Panel Principal
            </h2>
            
            {/* Estadísticas principales */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: getResponsiveColumns('stats'),
              gap: getResponsiveGap('8px', '12px', '16px'),
              marginBottom: getResponsiveSize('16px', '20px', '24px')
            }}>
              <div style={{ 
                padding: getResponsivePadding('12px', '16px', '20px'),
                backgroundColor: '#28a745', 
                color: 'white', 
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                minHeight: getResponsiveSize('80px', '90px', '100px'),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <h3 style={{ 
                  margin: '0 0 8px 0',
                  fontSize: getResponsiveSize('14px', '16px', '18px'),
                  fontWeight: '600'
                }}>💰 Ventas del Día</h3>
                <h2 style={{ 
                  margin: '0',
                  fontSize: getResponsiveSize('18px', '22px', '28px'),
                  fontWeight: 'bold'
                }}>${formatearNumero(totalDelDia)}</h2>
              </div>
              
              <div style={{ 
                padding: getResponsivePadding('12px', '16px', '20px'),
                backgroundColor: '#007bff', 
                color: 'white', 
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                minHeight: getResponsiveSize('80px', '90px', '100px'),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <h3 style={{ 
                  margin: '0 0 8px 0',
                  fontSize: getResponsiveSize('14px', '16px', '18px'),
                  fontWeight: '600'
                }}>📈 Total de Ventas</h3>
                <h2 style={{ 
                  margin: '0',
                  fontSize: getResponsiveSize('18px', '22px', '28px'),
                  fontWeight: 'bold'
                }}>{formatearNumero(ventasDelDia.length)}</h2>
              </div>
              
              <div style={{ 
                padding: getResponsivePadding('12px', '16px', '20px'),
                backgroundColor: '#17a2b8', 
                color: 'white', 
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                minHeight: getResponsiveSize('80px', '90px', '100px'),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gridColumn: isSmallMobile() ? 'span 1' : isMobile() ? 'span 2' : 'span 1'
              }}>
                <h3 style={{ 
                  margin: '0 0 8px 0',
                  fontSize: getResponsiveSize('14px', '16px', '18px'),
                  fontWeight: '600'
                }}>📊 Promedio por Venta</h3>
                <h2 style={{ 
                  margin: '0',
                  fontSize: getResponsiveSize('18px', '22px', '28px'),
                  fontWeight: 'bold'
                }}>${formatearNumero(ventasDelDia.length ? totalDelDia / ventasDelDia.length : 0)}</h2>
              </div>
            </div>

            {/* Ventas por Método de Pago */}
            <h3 style={{ 
              marginTop: getResponsiveSize('20px', '24px', '28px'),
              marginBottom: getResponsiveSize('12px', '16px', '20px'),
              fontSize: getResponsiveSize('16px', '18px', '20px'),
              textAlign: isMobile() ? 'center' : 'left'
            }}>💳 Ventas por Método de Pago</h3>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: getResponsiveColumns('payment-methods'),
              gap: getResponsiveGap('8px', '12px', '16px'),
              marginBottom: getResponsiveSize('20px', '24px', '28px')
            }}>
              <div style={{ 
                padding: getResponsivePadding('12px', '14px', '16px'),
                backgroundColor: '#198754',
                color: 'white',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                minHeight: getResponsiveSize('70px', '80px', '90px'),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <h4 style={{ 
                  margin: '0 0 6px 0', 
                  fontSize: getResponsiveSize('12px', '14px', '16px'),
                  fontWeight: '600'
                }}>💵 Efectivo</h4>
                <p style={{ 
                  margin: 0, 
                  fontSize: getResponsiveSize('16px', '18px', '20px'),
                  fontWeight: 'bold' 
                }}>
                  ${formatearNumero(metodoPagoStats.efectivo)}
                </p>
              </div>
              
              <div style={{ 
                padding: getResponsivePadding('12px', '14px', '16px'),
                backgroundColor: '#0d6efd',
                color: 'white',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                minHeight: getResponsiveSize('70px', '80px', '90px'),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <h4 style={{ 
                  margin: '0 0 6px 0', 
                  fontSize: getResponsiveSize('12px', '14px', '16px'),
                  fontWeight: '600'
                }}>💳 Tarjeta</h4>
                <p style={{ 
                  margin: 0, 
                  fontSize: getResponsiveSize('16px', '18px', '20px'),
                  fontWeight: 'bold' 
                }}>
                  ${formatearNumero(metodoPagoStats.tarjeta)}
                </p>
              </div>
              
              <div style={{ 
                padding: getResponsivePadding('12px', '14px', '16px'),
                backgroundColor: '#6f42c1',
                color: 'white',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                minHeight: getResponsiveSize('70px', '80px', '90px'),
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <h4 style={{ 
                  margin: '0 0 6px 0', 
                  fontSize: getResponsiveSize('12px', '14px', '16px'),
                  fontWeight: '600'
                }}>🏦 Transferencia</h4>
                <p style={{ 
                  margin: 0, 
                  fontSize: getResponsiveSize('16px', '18px', '20px'),
                  fontWeight: 'bold' 
                }}>
                  ${formatearNumero(metodoPagoStats.transferencia)}
                </p>
              </div>
            </div>

            {/* Top Productos Section */}
            <h3 style={{ 
              marginTop: getResponsiveSize('20px', '24px', '28px'),
              marginBottom: getResponsiveSize('12px', '16px', '20px'),
              fontSize: getResponsiveSize('16px', '18px', '20px'),
              textAlign: isMobile() ? 'center' : 'left'
            }}>🏆 Top 3 Productos (Por Cantidad)</h3>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: getResponsiveColumns('products'),
              gap: getResponsiveGap('8px', '12px', '16px'),
              marginBottom: getResponsiveSize('20px', '24px', '28px')
            }}>
              {topProductos.map((producto, index) => (
                <div key={producto.id} style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: getResponsivePadding('12px', '14px', '16px'),
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: getResponsiveSize('8px', '10px', '12px'),
                  minHeight: getResponsiveSize('180px', '200px', '220px'),
                  border: index === 0 ? '2px solid #ffd700' : index === 1 ? '2px solid #c0c0c0' : '2px solid #cd7f32',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Ribbon for top positions */}
                  {index < 3 && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '-20px',
                      backgroundColor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : '#cd7f32',
                      color: index === 1 ? '#333' : 'white',
                      padding: '4px 24px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      transform: 'rotate(45deg)',
                      minWidth: '60px',
                      textAlign: 'center'
                    }}>
                      #{index + 1}
                    </div>
                  )}
                  
                  <div style={{
                    backgroundColor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : '#cd7f32',
                    color: index === 1 ? '#333' : 'white',
                    width: getResponsiveSize('28px', '32px', '36px'),
                    height: getResponsiveSize('28px', '32px', '36px'),
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: getResponsiveSize('14px', '16px', '18px'),
                    fontWeight: 'bold',
                    marginTop: '8px'
                  }}>
                    {index + 1}
                  </div>
                  
                  {producto.imagenURL && (
                    <img 
                      src={producto.imagenURL}
                      alt={producto.nombre}
                      style={{
                        width: getResponsiveSize('60px', '70px', '80px'),
                        height: getResponsiveSize('60px', '70px', '80px'),
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '2px solid #f8f9fa'
                      }}
                    />
                  )}
                  
                  <h4 style={{ 
                    margin: '0', 
                    textAlign: 'center',
                    fontSize: getResponsiveSize('12px', '14px', '16px'),
                    lineHeight: '1.2',
                    maxHeight: '2.4em',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}>{producto.nombre}</h4>
                  
                  <p style={{ 
                    margin: '0',
                    fontSize: getResponsiveSize('14px', '15px', '16px'),
                    fontWeight: 'bold',
                    color: '#28a745',
                    textAlign: 'center'
                  }}>
                    🏅 {formatearNumero(producto.cantidadTotal)} vendidos
                  </p>
                  
                  <p style={{ 
                    margin: '0', 
                    color: '#666',
                    fontSize: getResponsiveSize('11px', '12px', '13px'),
                    textAlign: 'center'
                  }}>
                    💰 ${formatearNumero(producto.ventasTotal)}
                  </p>
                </div>
              ))}
            </div>

            {/* Últimas Ventas */}
            <h3 style={{ 
              marginTop: getResponsiveSize('20px', '24px', '28px'),
              marginBottom: getResponsiveSize('12px', '16px', '20px'),
              fontSize: getResponsiveSize('16px', '18px', '20px'),
              textAlign: isMobile() ? 'center' : 'left'
            }}>📋 Últimas Ventas</h3>
            <div style={{ 
              maxHeight: getResponsiveSize('300px', '350px', '400px'),
              overflowY: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa'
            }}>
              {ventasDelDia.slice(-5).reverse().map(venta => (
                <div key={venta.id} style={{ 
                  padding: getResponsivePadding('8px', '10px', '12px'),
                  border: '1px solid #dee2e6',
                  marginBottom: '8px',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  margin: '8px'
                }}>
                  <p style={{ 
                    margin: '0 0 4px 0',
                    fontSize: getResponsiveSize('14px', '15px', '16px'),
                    fontWeight: 'bold'
                  }}>💰 Total: ${formatearNumero(venta.total)}</p>
                  
                  {/* Mostrar métodos de pago */}
                  {venta.metodoPago === 'multiple' && venta.metodosPago ? (
                    <div>
                      <p>Métodos: </p>
                      <div style={{ marginLeft: '10px' }}>
                        {venta.metodosPago.map((pago, index) => (
                          <span key={index} style={{ 
                            display: 'inline-block',
                            padding: '2px 6px',
                            backgroundColor: '#6f42c1',
                            color: 'white',
                            borderRadius: '10px',
                            fontSize: '10px',
                            marginRight: '4px',
                            marginBottom: '2px'
                          }}>
                            {pago.metodoPago.charAt(0).toUpperCase() + pago.metodoPago.slice(1)}: ${formatearNumero(pago.monto)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p>
                      Método: <span style={{ 
                        display: 'inline-block',
                        padding: '2px 8px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        borderRadius: '12px',
                        fontSize: '12px'
                      }}>
                        {venta.metodoPago?.charAt(0).toUpperCase() + venta.metodoPago?.slice(1) || 'Efectivo'}
                      </span>
                    </p>
                  )}
                  
                  <p>Hora: {formatearFecha(venta.fecha)}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'productos':
        // Filtrar productos por categoría y búsqueda
        const productosFiltrados = productos.filter(producto => {
          const coincideBusqueda = producto.nombre.toLowerCase().includes(busqueda.toLowerCase());
          const coincideCategoria = filtroCategoria === 'Todas' || producto.categoria === filtroCategoria;
          return coincideBusqueda && coincideCategoria;
        });

        // Agrupar productos por categoría si no hay filtro específico
        const productosPorCategoria = {};
        if (filtroCategoria === 'Todas') {
          categorias.forEach(cat => {
            productosPorCategoria[cat.nombre] = productosFiltrados.filter(
              p => p.categoria === cat.nombre
            );
          });
          
          // Añadir productos sin categoría al final
          const sinCategoria = productosFiltrados.filter(
            p => !categorias.some(cat => cat.nombre === p.categoria)
          );
          if (sinCategoria.length > 0) {
            productosPorCategoria['Sin categoría'] = sinCategoria;
          }
        }

        return (
          <div 
            className="products-container"
            style={{ 
              padding: getResponsivePadding('8px', '12px', '16px'),
              display: 'flex', 
              flexDirection: isMobile() ? 'column' : 'row',
              gap: getResponsiveGap('12px', '16px', '20px'),
              height: isMobile() ? 'auto' : '100vh',
              overflow: 'hidden'
            }}
          >
            {/* Catálogo de productos */}
            <div style={{ 
              flex: isMobile() ? 'none' : 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Header responsive */}
              <h2 className={isMobile() ? 'vendedor-section-title' : ''} style={{
                fontSize: getResponsiveSize('20px', '22px', '24px'),
                marginBottom: getResponsiveSize('12px', '16px', '20px'),
                color: '#007bff',
                textAlign: isMobile() ? 'center' : 'left',
                borderBottom: '2px solid #007bff',
                paddingBottom: '8px'
              }}>
                🛒 Catálogo de Productos
              </h2>

              {/* Barra de búsqueda y filtros responsive */}
              <div 
                className="filter-grid"
                style={{ 
                  marginBottom: getResponsiveSize('16px', '20px', '24px'),
                  display: 'grid',
                  gridTemplateColumns: isMobile() ? '1fr' : '1fr auto',
                  gap: getResponsiveSize('8px', '12px', '16px'),
                  padding: getResponsivePadding('12px', '16px', '20px'),
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  border: '2px solid #dee2e6',
                  boxShadow: '0 2px 8px rgba(0, 123, 255, 0.1)'
                }}
              >
                <input
                  type="text"
                  placeholder="🔍 Buscar productos..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="touch-friendly"
                  style={{
                    padding: getResponsivePadding('10px', '12px', '14px'),
                    borderRadius: '8px',
                    border: '2px solid #dee2e6',
                    fontSize: getResponsiveSize('14px', '15px', '16px'),
                    outline: 'none',
                    transition: 'all 0.2s',
                    boxSizing: 'border-box',
                    backgroundColor: 'white',
                    color: '#333'
                  }}
                />
                
                <select
                  value={filtroCategoria}
                  onChange={(e) => setFiltroCategoria(e.target.value)}
                  className="touch-friendly"
                  style={{
                    padding: getResponsivePadding('10px', '12px', '14px'),
                    borderRadius: '8px',
                    border: '2px solid #dee2e6',
                    fontSize: getResponsiveSize('14px', '15px', '16px'),
                    backgroundColor: 'white',
                    outline: 'none',
                    minWidth: isMobile() ? '100%' : '200px',
                    boxSizing: 'border-box',
                    color: '#333',
                    fontWeight: '500'
                  }}
                >
                  <option value="Todas">📋 Todas las categorías</option>
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.nombre}>{cat.nombre}</option>
                  ))}
                </select>
              </div>
              
              <h2 className={isMobile() ? 'vendedor-section-title' : ''} style={{
                margin: `0 0 ${getResponsiveSize('12px', '16px', '20px')} 0`,
                fontSize: getResponsiveSize('18px', '20px', '24px'),
                textAlign: isMobile() ? 'center' : 'left',
                color: '#007bff'
              }}>📦 Catálogo de Productos</h2>

              {/* Container with scroll */}
              <div style={{ 
                flex: 1,
                overflow: 'auto',
                paddingRight: isMobile() ? '0' : '8px'
              }}>
                {filtroCategoria !== 'Todas' ? (
                  // Vista de productos filtrados por una categoría específica
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: getResponsiveColumns('products'),
                    gap: getResponsiveGap('6px', '8px', '10px'),
                    paddingBottom: isMobile() ? '80px' : '20px'
                  }}>
                    {productosFiltrados.map(producto => (
                      <ProductoCard 
                        key={producto.id} 
                        producto={producto} 
                        agregarProductoVenta={agregarProductoVenta}
                        formatearNumero={formatearNumero}
                        isMobile={isMobile}
                      />
                    ))}
                  </div>
                ) : (
                  // Vista agrupada por categorías
                  <div style={{ paddingBottom: isMobile() ? '80px' : '20px' }}>
                    {Object.keys(productosPorCategoria).map(categoria => 
                      productosPorCategoria[categoria].length > 0 && (
                        <div key={categoria} style={{ 
                          marginBottom: getResponsiveSize('20px', '25px', '30px')
                        }}>
                          <h3 style={{ 
                            borderBottom: '3px solid #28a745', 
                            paddingBottom: '8px',
                            marginBottom: getResponsiveSize('12px', '15px', '18px'),
                            fontSize: getResponsiveSize('14px', '16px', '18px'),
                            color: '#007bff',
                            fontWeight: '600'
                          }}>
                            📂 {categoria}
                          </h3>
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: getResponsiveColumns('products'),
                            gap: getResponsiveGap('6px', '8px', '10px')
                          }}>
                            {productosPorCategoria[categoria].map(producto => (
                              <ProductoCard 
                                key={producto.id} 
                                producto={producto} 
                                agregarProductoVenta={agregarProductoVenta}
                                formatearNumero={formatearNumero}
                                isMobile={isMobile}
                              />
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Panel de venta actual responsive con tema moderno */}
            <div 
              className="card"
              style={{ 
                width: isMobile() ? '100%' : '360px',
                minWidth: isMobile() ? '100%' : '360px',
                maxWidth: isMobile() ? '100%' : '360px',
                height: isMobile() ? 'auto' : 'calc(100vh - 20px)',
                maxHeight: isMobile() ? '70vh' : 'calc(100vh - 20px)',
                position: isMobile() ? 'fixed' : 'sticky',
                top: isMobile() ? 'auto' : '10px',
                bottom: isMobile() ? '0' : 'auto',
                left: isMobile() ? '0' : 'auto',
                backgroundColor: '#ffffff',
                padding: getResponsivePadding('12px', '16px', '20px'),
                boxShadow: isMobile() ? '0 -4px 12px rgba(0, 123, 255, 0.2)' : '0 4px 12px rgba(0, 123, 255, 0.15)',
                zIndex: isMobile() ? 1002 : 1000, // Asegurar que esté debajo del header en móvil
                overflowY: 'auto',
                display: isMobile() && menuMovilAbierto ? 'none' : 'flex', // Ocultar cuando el menú móvil esté abierto
                flexDirection: 'column',
                marginLeft: 'auto',
                borderRadius: isMobile() ? '16px 16px 0 0' : '12px',
                border: '2px solid #dee2e6',
                borderBottom: isMobile() ? 'none' : '2px solid #dee2e6'
              }}
            >
              <h3 style={{ 
                margin: '0 0 16px 0',
                fontSize: getResponsiveSize('18px', '20px', '22px'),
                textAlign: 'center',
                color: '#007bff',
                borderBottom: '2px solid #007bff',
                paddingBottom: '10px',
                fontWeight: 'bold'
              }}>
                � Carrito de Compras
                {ventaActual.mesa && (
                  <div style={{
                    fontSize: getResponsiveSize('12px', '13px', '14px'),
                    color: '#28a745',
                    marginTop: '6px',
                    fontWeight: 'normal',
                    textAlign: 'center',
                    backgroundColor: '#e8f5e9',
                    padding: '4px 8px',
                    borderRadius: '8px'
                  }}>
                    🪑 Mesa: {ventaActual.mesa.numero}
                  </div>
                )}
                {cuentaEnProceso && (
                  <div style={{
                    fontSize: getResponsiveSize('12px', '13px', '14px'),
                    color: '#495057',
                    marginTop: '6px',
                    fontWeight: 'normal',
                    textAlign: 'center',
                    backgroundColor: '#e9ecef',
                    padding: '4px 8px',
                    borderRadius: '8px'
                  }}>
                    🍽️ Procesando: {cuentaEnProceso.nombre}
                  </div>
                )}
              </h3>
              
              {ventaActual.productos.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  color: '#6c757d', 
                  margin: getResponsiveSize('20px 0', '24px 0', '28px 0'),
                  padding: getResponsivePadding('20px', '24px', '28px'),
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  border: '2px dashed #dee2e6'
                }}>
                  <div style={{ 
                    fontSize: getResponsiveSize('36px', '40px', '44px'),
                    marginBottom: '12px'
                  }}>🛒</div>
                  <p style={{ 
                    margin: '0',
                    fontSize: getResponsiveSize('14px', '15px', '16px'),
                    lineHeight: '1.4',
                    fontWeight: '500'
                  }}>
                    Agrega productos para comenzar tu venta
                  </p>
                </div>
              ) : (
                <>
                  {/* Lista de productos con scroll mejorada */}
                  <div style={{ 
                    flex: 1,
                    maxHeight: getResponsiveSize('280px', '350px', '420px'),
                    minHeight: getResponsiveSize('180px', '220px', '280px'),
                    overflowY: 'auto',
                    marginBottom: getResponsiveSize('12px', '16px', '20px'),
                    border: '2px solid #dee2e6',
                    borderRadius: '12px',
                    backgroundColor: '#f8f9fa',
                    padding: '4px'
                  }}>
                    {ventaActual.productos.map(producto => (
                      <div key={producto.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: getResponsivePadding('12px', '14px', '16px'),
                        borderBottom: '1px solid #dee2e6',
                        backgroundColor: '#ffffff',
                        margin: '4px',
                        borderRadius: '8px',
                        minHeight: getResponsiveSize('60px', '70px', '80px'),
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{ flex: 1, paddingRight: '8px' }}>
                          <p style={{ 
                            margin: '0 0 4px 0', 
                            fontWeight: 'bold', 
                            fontSize: getResponsiveSize('14px', '15px', '16px'),
                            color: '#333',
                            lineHeight: '1.3',
                            wordBreak: 'break-word'
                          }}>
                            {producto.nombre}
                          </p>
                          <p style={{ 
                            margin: '0', 
                            fontSize: getResponsiveSize('12px', '13px', '14px'), 
                            color: '#6c757d',
                            lineHeight: '1.2'
                          }}>
                            ${formatearNumero(producto.precio)} × {producto.cantidadVenta} unidad{producto.cantidadVenta > 1 ? 'es' : ''}
                          </p>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: getResponsiveSize('4px', '6px', '8px'),
                          minWidth: 'fit-content'
                        }}>
                          <span style={{ 
                            fontSize: getResponsiveSize('14px', '15px', '16px'), 
                            fontWeight: 'bold',
                            color: '#28a745',
                            textAlign: 'right',
                            whiteSpace: 'nowrap'
                          }}>
                            ${formatearNumero(producto.precio * producto.cantidadVenta)}
                          </span>
                          <button
                            onClick={() => quitarProductoVenta(producto.id)}
                            style={{
                              padding: getResponsivePadding('6px', '7px', '8px'),
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: getResponsiveSize('11px', '12px', '13px'),
                              width: getResponsiveSize('28px', '30px', '32px'),
                              height: getResponsiveSize('28px', '30px', '32px'),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                              fontWeight: 'bold',
                              flexShrink: 0
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                            title="Eliminar producto"
                          >
                            ✖
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Panel de pago fijo con tema moderno */}
                  <div style={{ 
                    backgroundColor: '#f8f9fa',
                    padding: getResponsivePadding('12px', '15px', '18px'),
                    borderRadius: '12px',
                    border: '2px solid #dee2e6',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                  }}>
                    <h4 style={{ 
                      margin: '0 0 10px 0', 
                      fontSize: getResponsiveSize('16px', '18px', '20px'),
                      color: '#007bff',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      padding: getResponsivePadding('8px', '10px', '12px'),
                      backgroundColor: '#ffffff',
                      borderRadius: '8px',
                      border: '2px solid #007bff'
                    }}>
                      💰 Total: ${formatearNumero(ventaActual.total)}
                    </h4>
                    
                    {/* Selector de Mesa */}
                    <div style={{
                      marginBottom: '15px',
                      borderRadius: '8px',
                      border: '2px solid #e9ecef',
                      padding: getResponsivePadding('12px', '14px', '16px'),
                      backgroundColor: '#f8f9fa'
                    }}>
                      <label 
                        htmlFor="mesa-select" 
                        style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontSize: getResponsiveSize('12px', '13px', '14px'),
                          fontWeight: 'bold',
                          color: '#495057'
                        }}
                      >
                        🪑 Seleccionar Mesa:
                      </label>
                      <select
                        id="mesa-select"
                        value={ventaActual.mesa?.id || ""}
                        onChange={(e) => cambiarMesaSeleccionada(e.target.value)}
                        style={{
                          width: '100%',
                          padding: getResponsivePadding('8px', '10px', '12px'),
                          borderRadius: '8px',
                          border: '2px solid #dee2e6',
                          fontSize: getResponsiveSize('12px', '13px', '14px'),
                          backgroundColor: '#ffffff',
                          color: '#333',
                          fontWeight: '500'
                        }}
                      >
                        <option value="">-- Sin mesa --</option>
                        {mesas.map(mesa => (
                          <option key={mesa.id} value={mesa.id}>
                            Mesa {mesa.numero} {mesa.descripcion ? `(${mesa.descripcion})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Mostrar resumen si hay pago múltiple activo con tema moderno */}
                    {pagoMultiple.activo && (
                      <div style={{
                        backgroundColor: '#f8f9fa',
                        border: '2px solid #dee2e6',
                        borderRadius: '8px',
                        padding: getResponsivePadding('8px', '10px', '12px'),
                        marginBottom: '10px',
                        fontSize: getResponsiveSize('12px', '13px', '14px')
                      }}>
                        <div style={{ 
                          fontWeight: 'bold', 
                          marginBottom: '6px',
                          color: '#007bff',
                          textAlign: 'center'
                        }}>
                          💳 Pago Múltiple Activo
                        </div>
                        <div style={{ 
                          color: '#495057',
                          textAlign: 'center',
                          fontSize: getResponsiveSize('11px', '12px', '13px'),
                          lineHeight: '1.4'
                        }}>
                          Pagado: ${formatearNumero(pagoMultiple.totalPagado)} | 
                          Restante: ${formatearNumero(pagoMultiple.restante)}
                        </div>
                      </div>
                    )}
                    
                    {!pagoMultiple.activo ? (
                      // Panel de pago único (comportamiento original)
                      <>
                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '6px', 
                            fontSize: getResponsiveSize('12px', '13px', '14px'),
                            fontWeight: 'bold',
                            color: '#007bff'
                          }}>
                            💰 Método de Pago:
                          </label>
                          <select
                            value={ventaActual.metodoPago}
                            onChange={(e) => setVentaActual({ ...ventaActual, metodoPago: e.target.value })}
                            style={{
                              width: '100%',
                              padding: getResponsivePadding('8px', '10px', '12px'),
                              marginBottom: '8px',
                              borderRadius: '8px',
                              border: '2px solid #dee2e6',
                              fontSize: getResponsiveSize('12px', '13px', '14px'),
                              backgroundColor: '#ffffff',
                              color: '#333',
                              fontWeight: '500'
                            }}
                          >
                            <option value="efectivo">💵 Efectivo</option>
                            <option value="tarjeta">💳 Tarjeta</option>
                            <option value="transferencia">🏦 Transferencia</option>
                          </select>
                        </div>

                        {ventaActual.metodoPago === 'efectivo' && (
                          <>
                            <input
                              type="number"
                              placeholder="💵 Efectivo recibido"
                              value={ventaActual.efectivo}
                              onChange={(e) => {
                                // Solo procesar valores positivos
                                const valor = parseFloat(e.target.value);
                                if (!isNaN(valor) && valor >= 0) {
                                  calcularCambio(valor);
                                } else if (e.target.value === '') {
                                  // Permitir borrar el campo
                                  calcularCambio('');
                                }
                              }}
                              onWheel={(e) => e.target.blur()} // Evita el scroll
                              min="0" // Restringe a valores positivos
                              style={{
                                width: '100%',
                                padding: getResponsivePadding('8px', '10px', '12px'),
                                marginBottom: '8px',
                                borderRadius: '8px',
                                border: '2px solid #dee2e6',
                                fontSize: getResponsiveSize('12px', '13px', '14px'),
                                boxSizing: 'border-box',
                                backgroundColor: '#ffffff',
                                color: '#333'
                              }}
                            />
                            <p style={{ 
                              margin: '0 0 8px 0', 
                              fontSize: getResponsiveSize('12px', '13px', '14px'),
                              textAlign: 'center',
                              backgroundColor: '#f8f9fa',
                              padding: getResponsivePadding('6px', '8px', '10px'),
                              borderRadius: '6px',
                              border: '1px solid #28a745',
                              fontWeight: 'bold',
                              color: '#28a745'
                            }}>
                              💸 Cambio: ${formatearNumero(ventaActual.cambio)}
                            </p>
                          </>
                        )}

                        {/* Botón para finalizar venta - MOVIDO ARRIBA */}
                        <button
                          onClick={registrarVenta}
                          disabled={(pagoMultiple.activo && pagoMultiple.restante > 0) || procesandoVenta}
                          style={{
                            width: '100%',
                            padding: getResponsivePadding('12px', '14px', '16px'),
                            backgroundColor: procesandoVenta 
                              ? '#6c757d'
                              : (pagoMultiple.activo && pagoMultiple.restante > 0) 
                                ? '#6c757d' 
                                : '#6f42c1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: procesandoVenta || (pagoMultiple.activo && pagoMultiple.restante > 0) 
                              ? 'not-allowed' 
                              : 'pointer',
                            opacity: procesandoVenta || (pagoMultiple.activo && pagoMultiple.restante > 0) 
                              ? 0.6 
                              : 1,
                            fontSize: getResponsiveSize('13px', '14px', '15px'),
                            fontWeight: 'bold',
                            minHeight: getResponsiveSize('44px', '48px', '52px'),
                            transition: 'all 0.3s ease',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => {
                            if (!procesandoVenta && !(pagoMultiple.activo && pagoMultiple.restante > 0)) {
                              e.target.style.backgroundColor = '#5a32a3';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!procesandoVenta && !(pagoMultiple.activo && pagoMultiple.restante > 0)) {
                              e.target.style.backgroundColor = '#6f42c1';
                            }
                          }}
                        >
                          {procesandoVenta 
                            ? '⏳ Procesando...'
                            : pagoMultiple.activo && pagoMultiple.restante > 0 
                              ? `💰 Falta $${formatearNumero(pagoMultiple.restante)}`
                              : '☕ Finalizar Venta'
                          }
                        </button>

                        {/* Botón para división de cuentas */}
                        <button
                          onClick={iniciarDivisionCuenta}
                          style={{
                            width: '100%',
                            padding: getResponsivePadding('12px', '14px', '16px'),
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            marginBottom: '8px',
                            fontSize: getResponsiveSize('13px', '14px', '15px'),
                            fontWeight: 'bold',
                            transition: 'all 0.3s ease',
                            minHeight: getResponsiveSize('44px', '48px', '52px'),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
                        >
                          🍽️ División de Cuenta
                        </button>

                        {/* Botón para activar pago múltiple */}
                        <button
                          onClick={() => {
                            activarPagoMultiple();
                            setMostrarPagoMultiple(true);
                          }}
                          style={{
                            width: '100%',
                            padding: getResponsivePadding('12px', '14px', '16px'),
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            marginBottom: '8px',
                            fontSize: getResponsiveSize('13px', '14px', '15px'),
                            fontWeight: 'bold',
                            transition: 'all 0.3s ease',
                            minHeight: getResponsiveSize('44px', '48px', '52px'),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
                        >
                          💳 Pago Múltiple
                        </button>
                      </>
                    ) : (
                      // Panel cuando pago múltiple está activo
                      <>
                        {/* Botón para finalizar venta - MOVIDO ARRIBA */}
                        <button
                          onClick={registrarVenta}
                          disabled={(pagoMultiple.activo && pagoMultiple.restante > 0) || procesandoVenta}
                          style={{
                            width: '100%',
                            padding: getResponsivePadding('12px', '14px', '16px'),
                            backgroundColor: procesandoVenta 
                              ? '#6c757d'
                              : (pagoMultiple.activo && pagoMultiple.restante > 0) 
                                ? '#6c757d' 
                                : '#6f42c1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: procesandoVenta || (pagoMultiple.activo && pagoMultiple.restante > 0) 
                              ? 'not-allowed' 
                              : 'pointer',
                            opacity: procesandoVenta || (pagoMultiple.activo && pagoMultiple.restante > 0) 
                              ? 0.6 
                              : 1,
                            fontSize: getResponsiveSize('13px', '14px', '15px'),
                            fontWeight: 'bold',
                            minHeight: getResponsiveSize('44px', '48px', '52px'),
                            transition: 'all 0.3s ease',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => {
                            if (!procesandoVenta && !(pagoMultiple.activo && pagoMultiple.restante > 0)) {
                              e.target.style.backgroundColor = '#5a32a3';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!procesandoVenta && !(pagoMultiple.activo && pagoMultiple.restante > 0)) {
                              e.target.style.backgroundColor = '#6f42c1';
                            }
                          }}
                        >
                          {procesandoVenta 
                            ? '⏳ Procesando...'
                            : pagoMultiple.activo && pagoMultiple.restante > 0 
                              ? `💰 Falta $${formatearNumero(pagoMultiple.restante)}`
                              : '☕ Finalizar Venta'
                          }
                        </button>
                        
                        <button
                          onClick={() => setMostrarPagoMultiple(true)}
                          style={{
                            width: '100%',
                            padding: getResponsivePadding('12px', '14px', '16px'),
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            marginBottom: '8px',
                            fontSize: getResponsiveSize('13px', '14px', '15px'),
                            fontWeight: 'bold',
                            transition: 'all 0.3s ease',
                            minHeight: getResponsiveSize('44px', '48px', '52px'),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#138496'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#17a2b8'}
                        >
                          ⚙️ Gestionar Pagos
                        </button>

                        <button
                          onClick={desactivarPagoMultiple}
                          style={{
                            width: '100%',
                            padding: getResponsivePadding('12px', '14px', '16px'),
                            backgroundColor: '#ffc107',
                            color: '#212529',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            marginBottom: '8px',
                            fontSize: getResponsiveSize('13px', '14px', '15px'),
                            fontWeight: 'bold',
                            transition: 'all 0.3s ease',
                            minHeight: getResponsiveSize('44px', '48px', '52px'),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#e0a800'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#ffc107'}
                        >
                          ❌ Cancelar Pago Múltiple
                        </button>
                      </>
                    )}
                    
                    {/* Botón para limpiar carrito */}
                    <button
                      onClick={() => {
                        setVentaActual({
                          productos: [],
                          total: 0,
                          efectivo: 0,
                          cambio: 0,
                          metodoPago: 'efectivo',
                          mesa: null // Limpiar mesa seleccionada
                        });
                        setPagoMultiple({
                          activo: false,
                          pagos: [],
                          totalPagado: 0,
                          restante: 0
                        });
                        showToast('Carrito limpiado', 'info');
                      }}
                      style={{
                        width: '100%',
                        padding: getResponsivePadding('12px', '14px', '16px'),
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: getResponsiveSize('13px', '14px', '15px'),
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease',
                        minHeight: getResponsiveSize('44px', '48px', '52px'),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                    >
                      🗑️ Limpiar Carrito
                    </button>
                  </div>
                </>
              )}

              {/* Panel de cuentas divididas */}
              {procesandoCuentas && cuentasDivididas.length > 0 && (
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: '#e8f4f8',
                  borderRadius: '8px',
                  border: '2px solid #17a2b8'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#17a2b8' }}>
                    🍽️ Cuentas Divididas ({cuentasDivididas.length})
                  </h4>
                  
                  <div style={{ marginBottom: '15px' }}>
                    <button
                      onClick={cancelarDivisionCuentas}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        float: 'right'
                      }}
                    >
                      ❌ Cancelar División
                    </button>
                    <div style={{ clear: 'both' }}></div>
                  </div>

                  {cuentasDivididas.map((cuenta, index) => {
                    const estaProcesada = cuentasProcesadas.includes(cuenta.id);
                    const estaEnProceso = cuentaEnProceso && cuentaEnProceso.id === cuenta.id;
                    
                    return (
                      <div key={cuenta.id} style={{
                        backgroundColor: estaProcesada ? '#d4edda' : estaEnProceso ? '#fff3cd' : 'white',
                        padding: '12px',
                        marginBottom: '10px',
                        borderRadius: '6px',
                        border: estaProcesada 
                          ? '1px solid #28a745' 
                          : estaEnProceso 
                            ? '1px solid #ffc107'
                            : '1px solid #17a2b8',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: 'bold', 
                            marginBottom: '5px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            {cuenta.nombre}
                            {estaProcesada && (
                              <span style={{ 
                                backgroundColor: '#28a745', 
                                color: 'white', 
                                padding: '2px 6px', 
                                borderRadius: '12px', 
                                fontSize: '10px' 
                              }}>
                                ✅ Procesada
                              </span>
                            )}
                            {estaEnProceso && (
                              <span style={{ 
                                backgroundColor: '#ffc107', 
                                color: 'black', 
                                padding: '2px 6px', 
                                borderRadius: '12px', 
                                fontSize: '10px' 
                              }}>
                                🔄 En Proceso
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '13px', color: '#6c757d' }}>
                            {cuenta.productos.length} productos • Total: ${formatearNumero(cuenta.total)}
                          </div>
                          <div style={{ fontSize: '12px', marginTop: '5px' }}>
                            {cuenta.productos.map(p => `${p.cantidadAsignada}x ${p.nombre}`).join(', ')}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {!estaProcesada && !estaEnProceso && (
                            <button
                              onClick={() => procesarCuentaIndividual(cuenta)}
                              style={{
                                padding: '8px 12px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              💳 Procesar Pago
                            </button>
                          )}
                          {estaEnProceso && (
                            <div style={{
                              padding: '8px 12px',
                              backgroundColor: '#ffc107',
                              color: 'black',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              Configure el pago ➡️
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );

      case 'ventas':
        return (
          <div className={isMobile() ? 'vendedor-content' : ''} style={{ 
            padding: getResponsivePadding('8px', '12px', '20px'),
            paddingTop: isMobile() ? '80px' : '20px' // Espacio para header fijo en móvil
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile() ? 'column' : 'row',
              justifyContent: 'space-between', 
              alignItems: isMobile() ? 'stretch' : 'center',
              gap: isMobile() ? '12px' : '0',
              marginBottom: getResponsiveSize('16px', '18px', '20px')
            }}>
              <h2 className={isMobile() ? 'vendedor-section-title' : ''} style={{
                margin: 0,
                fontSize: getResponsiveSize('20px', '22px', '24px'),
                color: '#2c3e50',
                fontWeight: '600'
              }}>📈 Historial de Ventas</h2>
              
              <div style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                backgroundColor: '#e8f5e8',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #c3e6c3'
              }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#2d5a2d' }}>
                  💰 Total: ${formatearNumero(totalDelDia)}
                </span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  ({historialVentas.length} ventas)
                </span>
              </div>
            </div>

            {historialVentas.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '2px dashed #dee2e6'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                <h3 style={{ color: '#6c757d', marginBottom: '8px' }}>No hay ventas registradas</h3>
                <p style={{ color: '#adb5bd', margin: 0 }}>
                  Las ventas aparecerán aquí una vez que se registren
                </p>
              </div>
            ) : (
              <div style={{ 
                maxHeight: isMobile() ? '60vh' : '600px', 
                overflowY: 'auto',
                paddingRight: '8px'
              }}>
                {[...historialVentas].reverse().map(venta => (
                  <div key={venta.id} style={{ 
                    border: '1px solid #e9ecef',
                    borderRadius: '12px',
                    padding: getResponsivePadding('12px', '15px', '20px'),
                    marginBottom: '16px',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}>
                    {/* Header de la venta */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: isMobile() ? 'column' : 'row',
                      justifyContent: 'space-between', 
                      alignItems: isMobile() ? 'flex-start' : 'center',
                      marginBottom: '15px',
                      paddingBottom: '10px',
                      borderBottom: '1px solid #f1f3f4'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <h4 style={{ 
                          margin: 0, 
                          color: '#007bff',
                          fontSize: getResponsiveSize('16px', '17px', '18px'),
                          fontWeight: '600'
                        }}>
                          🧾 #{venta.id.slice(-6).replace(/[^0-9a-zA-Z]/g, '').toUpperCase()}
                        </h4>
                        {venta.esCuentaDividida && (
                          <span style={{
                            backgroundColor: '#ffc107',
                            color: '#856404',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            CUENTA DIVIDIDA
                          </span>
                        )}
                        {venta.esVentaInterlocal && (
                          <span style={{
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            INTERLOCAL
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: getResponsiveSize('12px', '13px', '14px'),
                        color: '#6c757d',
                        marginTop: isMobile() ? '4px' : '0'
                      }}>
                        📅 {formatearFecha(venta.fecha)}
                      </div>
                    </div>

                    {/* Productos */}
                    <div style={{ marginBottom: '15px' }}>
                      <h5 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: getResponsiveSize('14px', '15px', '16px'),
                        color: '#495057',
                        fontWeight: '500'
                      }}>
                        🛍️ Productos ({venta.productos?.length || 0})
                      </h5>
                      <div style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        padding: '12px',
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        {venta.productos && venta.productos.map(prod => (
                          <div key={prod.id} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 0',
                            borderBottom: '1px solid #e9ecef'
                          }}>
                            <div style={{ flex: 1 }}>
                              <span style={{ 
                                fontWeight: '500',
                                fontSize: getResponsiveSize('13px', '14px', '15px')
                              }}>
                                {prod.nombre}
                              </span>
                              <div style={{ 
                                fontSize: '12px', 
                                color: '#6c757d',
                                marginTop: '2px'
                              }}>
                                ${formatearNumero(prod.precio)} × {prod.cantidadVenta}
                              </div>
                            </div>
                            <span style={{ 
                              fontWeight: '600',
                              color: '#28a745',
                              fontSize: getResponsiveSize('13px', '14px', '15px')
                            }}>
                              ${formatearNumero(prod.precio * prod.cantidadVenta)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Información de pago y vendedor */}
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: isMobile() ? '1fr' : '1fr 1fr',
                      gap: '15px',
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px'
                    }}>
                      {/* Información de pago */}
                      <div>
                        <h6 style={{ margin: '0 0 8px 0', color: '#495057', fontSize: '14px' }}>💳 Pago</h6>
                        
                        {venta.metodoPago === 'multiple' && venta.metodosPago ? (
                          <div>
                            {venta.metodosPago.map((pago, index) => (
                              <div key={index} style={{ marginBottom: '6px', fontSize: '13px' }}>
                                <span style={{
                                  backgroundColor: '#6f42c1',
                                  color: 'white',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  marginRight: '8px'
                                }}>
                                  {pago.metodoPago.charAt(0).toUpperCase() + pago.metodoPago.slice(1)}
                                </span>
                                <span>${formatearNumero(pago.monto)}</span>
                                {pago.metodoPago === 'efectivo' && pago.cambio > 0 && (
                                  <div style={{ fontSize: '11px', color: '#6c757d', marginLeft: '20px' }}>
                                    Cambio: ${formatearNumero(pago.cambio)}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div>
                            <div style={{ marginBottom: '4px' }}>
                              <span style={{
                                backgroundColor: '#007bff',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                marginRight: '8px'
                              }}>
                                {venta.metodoPago?.charAt(0).toUpperCase() + venta.metodoPago?.slice(1) || 'Efectivo'}
                              </span>
                              <span style={{ fontSize: '13px' }}>${formatearNumero(venta.total)}</span>
                            </div>
                            {venta.metodoPago === 'efectivo' && venta.cambio > 0 && (
                              <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                Efectivo: ${formatearNumero(venta.efectivo)} | Cambio: ${formatearNumero(venta.cambio)}
                              </div>
                            )}
                          </div>
                        )}
                        
                        <div style={{ 
                          marginTop: '8px', 
                          paddingTop: '8px', 
                          borderTop: '1px solid #dee2e6',
                          fontWeight: '600',
                          color: '#28a745',
                          fontSize: '15px'
                        }}>
                          💰 Total: ${formatearNumero(venta.total)}
                        </div>
                      </div>

                      {/* Información del personal */}
                      <div>
                        <h6 style={{ margin: '0 0 8px 0', color: '#495057', fontSize: '14px' }}>👥 Personal</h6>
                        
                        {venta.mesero ? (
                          <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                            <div><strong>🍽️ Mesero:</strong> {venta.mesero}</div>
                            <div><strong>💼 Procesado por:</strong> {venta.vendedorNombre}</div>
                            {venta.mesa && <div><strong>🪑 Mesa:</strong> {venta.mesa}</div>}
                          </div>
                        ) : (
                          <div style={{ fontSize: '13px' }}>
                            <strong>💼 Vendedor:</strong> {venta.vendedorNombre}
                          </div>
                        )}
                        
                        {venta.sucursal && (
                          <div style={{ 
                            marginTop: '8px', 
                            fontSize: '12px', 
                            color: '#6c757d' 
                          }}>
                            <strong>🏢 Sucursal:</strong> {venta.sucursal}
                          </div>
                        )}
                        
                        {venta.nombreCuenta && (
                          <div style={{ 
                            marginTop: '8px', 
                            fontSize: '12px', 
                            color: '#856404' 
                          }}>
                            <strong>👤 Cuenta:</strong> {venta.nombreCuenta}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'ordenes_meseros':
        return (
          <div className={isMobile() ? 'vendedor-content' : ''} style={{ 
            padding: getResponsivePadding('8px', '12px', '20px'),
            fontSize: getResponsiveSize('14px', '15px', '16px')
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile() ? 'column' : 'row',
              justifyContent: 'space-between', 
              alignItems: isMobile() ? 'stretch' : 'center',
              gap: isMobile() ? '12px' : '0',
              marginBottom: getResponsiveSize('16px', '18px', '20px')
            }}>
              <h2 className={isMobile() ? 'vendedor-section-title' : ''} style={{
                margin: '0',
                fontSize: getResponsiveSize('18px', '20px', '24px'),
                textAlign: isMobile() ? 'center' : 'left'
              }}>🍽️ Órdenes de Meseros</h2>
              <button
                onClick={cargarVentasPendientes}
                style={{
                  padding: getResponsivePadding('8px 12px', '8px 14px', '8px 16px'),
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: getResponsiveSize('12px', '13px', '14px'),
                  fontWeight: '500',
                  alignSelf: isMobile() ? 'center' : 'auto',
                  minWidth: isMobile() ? '160px' : 'auto'
                }}
              >
                🔄 Actualizar
              </button>
            </div>
            
            {ventasPendientes.length === 0 ? (
              <div style={{ 
                textAlign: 'center',
                padding: getResponsivePadding('24px', '32px', '40px'),
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #dee2e6',
                margin: '0 auto',
                maxWidth: '500px'
              }}>
                <div style={{ 
                  fontSize: getResponsiveSize('40px', '48px', '56px'),
                  marginBottom: '16px'
                }}>🍽️</div>
                <p style={{ 
                  fontSize: getResponsiveSize('16px', '17px', '18px'),
                  margin: '0 0 8px 0',
                  fontWeight: '600',
                  color: '#495057'
                }}>No hay órdenes pendientes de meseros</p>
                <p style={{ 
                  color: '#6c757d', 
                  margin: 0,
                  fontSize: getResponsiveSize('13px', '14px', '15px'),
                  lineHeight: '1.4'
                }}>
                  Las órdenes que los meseros envíen a cobrar aparecerán aquí
                </p>
              </div>
            ) : (
              <div style={{ 
                maxHeight: getResponsiveSize('500px', '600px', '700px'),
                overflowY: 'auto'
              }}>
                {ventasPendientes.map(orden => (
                  <div key={orden.id} style={{ 
                    border: '2px solid #ffc107',
                    borderRadius: '12px',
                    padding: getResponsivePadding('12px', '14px', '16px'),
                    marginBottom: getResponsiveSize('12px', '14px', '16px'),
                    backgroundColor: '#fff9c4',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: isMobile() ? 'column' : 'row',
                      justifyContent: 'space-between', 
                      marginBottom: getResponsiveSize('8px', '10px', '12px'),
                      gap: isMobile() ? '8px' : '0'
                    }}>
                      <h4 style={{
                        margin: '0',
                        fontSize: getResponsiveSize('16px', '18px', '20px'),
                        color: '#856404',
                        fontWeight: 'bold'
                      }}>🏷️ Mesa {orden.mesa}</h4>
                      <div style={{
                        display: 'flex',
                        flexDirection: isMobile() ? 'column' : 'row',
                        gap: isMobile() ? '4px' : '12px',
                        fontSize: getResponsiveSize('12px', '13px', '14px'),
                        color: '#6c757d'
                      }}>
                        <span style={{ fontWeight: '500' }}>👨‍🍳 {orden.mesero}</span>
                        <span>
                          📅 {orden.fecha?.seconds 
                            ? new Date(orden.fecha.seconds * 1000).toLocaleString()
                            : orden.fecha 
                              ? new Date(orden.fecha).toLocaleString()
                              : 'Fecha no disponible'
                          }
                        </span>
                      </div>
                    </div>
                    
                    {orden.productos && orden.productos.map(prod => (
                      <div key={prod.id} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        padding: '5px 0',
                        borderBottom: '1px solid #eee'
                      }}>
                        <span>{prod.nombre} x {prod.cantidadOrden || prod.cantidadVenta || 1}</span>
                        <span>${formatearNumero(prod.precio * (prod.cantidadOrden || prod.cantidadVenta || 1))}</span>
                      </div>
                    ))}
                    
                    {orden.notas && (
                      <div style={{ 
                        marginTop: '10px',
                        padding: '8px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}>
                        <strong>Notas:</strong> {orden.notas}
                      </div>
                    )}
                    
                    <div style={{ 
                      marginTop: '10px',
                      padding: '10px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <p style={{ margin: 0 }}><strong>Total: ${formatearNumero(orden.total)}</strong></p>
                        <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#28a745' }}>
                          ✅ Enviada a cobrar
                        </p>
                      </div>
                      <button
                        onClick={() => procesarOrdenMesero({
                          ...orden,
                          productos: orden.productos.map(p => ({
                            ...p,
                            cantidadVenta: p.cantidadOrden || p.cantidadVenta || 1
                          }))
                        })}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        💳 Procesar Cobro
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'ordenes_proceso':
        return (
          <div className={isMobile() ? 'vendedor-content' : ''} style={{ padding: '20px' }}>
            <h2 className={isMobile() ? 'vendedor-section-title' : ''}>🔄 Órdenes en Proceso</h2>
            {ordenesEnProceso.length === 0 ? (
              <p>No hay órdenes en proceso</p>
            ) : (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: isMobile() ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '15px',
                marginTop: '20px'
              }}>
                {ordenesEnProceso.map(orden => (
                  <div key={orden.id} style={{
                    border: orden.estado === 'lista_para_cobrar' 
                      ? '1px solid #28a745' 
                      : '1px solid #ffc107',
                    borderRadius: '12px',
                    padding: '15px',
                    backgroundColor: orden.estado === 'lista_para_cobrar' 
                      ? '#f8fff8'
                      : '#fff9e6',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {/* Cabecera de la tarjeta */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginBottom: '15px',
                      borderBottom: orden.estado === 'lista_para_cobrar' 
                        ? '1px solid #e6f5e6'
                        : '1px solid #fff3cd',
                      paddingBottom: '10px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        color: orden.estado === 'lista_para_cobrar' ? '#28a745' : '#ffc107'
                      }}>
                        <span style={{ 
                          backgroundColor: orden.estado === 'lista_para_cobrar' ? '#28a745' : '#ffc107', 
                          color: orden.estado === 'lista_para_cobrar' ? 'white' : 'black', 
                          width: '24px', 
                          height: '24px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>
                          {orden.mesa}
                        </span>
                        <h4 style={{ margin: 0 }}>Mesa {orden.mesa}</h4>
                      </div>
                      <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end'
                      }}>
                        <span style={{ 
                          backgroundColor: orden.estado === 'lista_para_cobrar' ? '#28a745' : '#ffc107',
                          color: orden.estado === 'lista_para_cobrar' ? 'white' : 'black',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          marginBottom: '5px'
                        }}>
                          {orden.estado === 'lista_para_cobrar' ? 'Lista para cobrar' : 'En preparación'}
                        </span>
                        <span style={{ 
                          color: '#666',
                          fontSize: '12px'
                        }}>
                          {orden.fecha?.seconds ? new Date(orden.fecha.seconds * 1000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                        </span>
                      </div>
                    </div>
                    
                    {/* Lista de productos */}
                    <div style={{ 
                      flex: 1,
                      marginBottom: '15px'
                    }}>
                      {orden.productos.map((prod, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          fontSize: '14px',
                          padding: '8px 0',
                          borderBottom: index < orden.productos.length - 1 ? '1px dashed #eee' : 'none'
                        }}>
                          <div style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}>
                            <span style={{
                              backgroundColor: orden.estado === 'lista_para_cobrar' ? '#e8f5e8' : '#fff3cd',
                              color: orden.estado === 'lista_para_cobrar' ? '#28a745' : '#ffc107',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {prod.cantidadOrden}
                            </span>
                            <span style={{ fontWeight: '500' }}>{prod.nombre}</span>
                          </div>
                          <span style={{ 
                            color: orden.estado === 'lista_para_cobrar' ? '#28a745' : '#ffc107',
                            fontWeight: '600'
                          }}>
                            ${formatearNumero(prod.precio * prod.cantidadOrden)}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Notas */}
                    {orden.notas && (
                      <div style={{ 
                        marginBottom: '15px',
                        padding: '10px',
                        backgroundColor: orden.estado === 'lista_para_cobrar' ? '#e8f5e8' : '#fff3cd',
                        borderRadius: '8px',
                        fontSize: '14px',
                        borderLeft: orden.estado === 'lista_para_cobrar' 
                          ? '3px solid #28a745' 
                          : '3px solid #ffc107'
                      }}>
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          marginBottom: '5px',
                          color: orden.estado === 'lista_para_cobrar' ? '#28a745' : '#ffc107',
                          fontWeight: '600'
                        }}>
                          <span>📝</span>
                          <span>Notas:</span>
                        </div>
                        <div style={{ 
                          paddingLeft: '20px', 
                          color: orden.estado === 'lista_para_cobrar' ? '#495057' : '#856404'
                        }}>
                          {orden.notas}
                        </div>
                      </div>
                    )}

                    {/* Footer con total y acciones */}
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderTop: orden.estado === 'lista_para_cobrar' 
                        ? '1px solid #e6f5e6' 
                        : '1px solid #fff3cd',
                      paddingTop: '15px',
                      flexWrap: 'wrap',
                      gap: '10px'
                    }}>
                      <div style={{ 
                        backgroundColor: orden.estado === 'lista_para_cobrar' ? '#e8f5e8' : '#fff3cd',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        fontWeight: 'bold',
                        color: '#212529'
                      }}>
                        Total: <span style={{ 
                          color: orden.estado === 'lista_para_cobrar' ? '#28a745' : '#ffc107'
                        }}>
                          ${formatearNumero(orden.total)}
                        </span>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        gap: '8px'
                      }}>
                        <button
                          onClick={() => imprimirOrden(orden)}
                          disabled={ordenesImpresas.has(orden.id)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: ordenesImpresas.has(orden.id) ? '#6c757d' : '#17a2b8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: ordenesImpresas.has(orden.id) ? 'not-allowed' : 'pointer',
                            fontSize: getResponsiveSize('12px', '13px', '14px'),
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            opacity: ordenesImpresas.has(orden.id) ? 0.6 : 1
                          }}
                        >
                          <span>{ordenesImpresas.has(orden.id) ? '✓' : '🖨️'}</span>
                          <span>{ordenesImpresas.has(orden.id) ? 'Impreso' : 'Imprimir'}</span>
                        </button>
                        
                        {/* Botón para resetear estado de impresión (solo si ya fue impreso) */}
                        {ordenesImpresas.has(orden.id) && (
                          <button
                            onClick={() => resetearEstadoImpresion(orden.id)}
                            title="Permitir imprimir nuevamente"
                            style={{
                              padding: '4px 6px',
                              backgroundColor: '#ffc107',
                              color: '#000',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '10px',
                              fontWeight: '500'
                            }}
                          >
                            🔄
                          </button>
                        )}
                        {orden.estado === 'lista_para_cobrar' && (
                          <button
                            onClick={() => procesarOrdenMesero({
                              ...orden,
                              productos: orden.productos.map(p => ({
                                ...p,
                                cantidadVenta: p.cantidadOrden
                              }))
                            })}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: getResponsiveSize('12px', '13px', '14px'),
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <span>💳</span>
                            <span>Procesar Cobro</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Componente de tarjeta de producto responsive con tema café
  const ProductoCard = ({ producto, agregarProductoVenta, formatearNumero, isMobile }) => (
    <div 
      className="card hover-effect"
      style={{ 
        border: (producto.cantidad !== null && producto.cantidad !== undefined && producto.cantidad <= 0) ? '2px solid #dc3545' : '2px solid #dee2e6',
        borderRadius: '12px',
        padding: getResponsivePadding('8px', '10px', '12px'),
        backgroundColor: (producto.cantidad !== null && producto.cantidad !== undefined && producto.cantidad <= 0) ? '#f8d7da' : '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        minHeight: getResponsiveSize('180px', '200px', '220px'),
        boxShadow: '0 4px 8px rgba(0, 123, 255, 0.1)',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        cursor: (producto.cantidad !== null && producto.cantidad !== undefined && producto.cantidad <= 0) ? 'not-allowed' : 'pointer'
      }}
    >
      {/* Stock indicator mejorado */}
      {producto.cantidad !== null && producto.cantidad !== undefined && producto.cantidad <= 5 && producto.cantidad > 0 && (
        <div style={{
          position: 'absolute',
          top: '6px',
          right: '6px',
          backgroundColor: '#ffc107',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '10px',
          fontSize: getResponsiveSize('9px', '10px', '11px'),
          fontWeight: 'bold',
          zIndex: 1,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}>
          ⚠️ Bajo
        </div>
      )}
      
      {producto.cantidad !== null && producto.cantidad !== undefined && producto.cantidad <= 0 && (
        <div style={{
          position: 'absolute',
          top: '6px',
          right: '6px',
          backgroundColor: '#A0522D',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '10px',
          fontSize: getResponsiveSize('9px', '10px', '11px'),
          fontWeight: 'bold',
          zIndex: 1,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}>
          ❌ Agotado
        </div>
      )}
      
      {producto.imagenURL && (
        <img 
          src={producto.imagenURL} 
          alt={producto.nombre}
          style={{ 
            width: '100%', 
            height: getResponsiveSize('70px', '80px', '90px'),
            objectFit: 'cover', 
            borderRadius: '8px',
            marginBottom: '8px',
            opacity: (producto.cantidad !== null && producto.cantidad !== undefined && producto.cantidad <= 0) ? 0.5 : 1,
            border: '1px solid #dee2e6'
          }}
        />
      )}
      
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        gap: '6px'
      }}>
        <h4 style={{ 
          margin: '0',
          fontSize: getResponsiveSize('12px', '13px', '14px'),
          fontWeight: '600',
          lineHeight: '1.2',
          maxHeight: '2.4em',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          color: (producto.cantidad !== null && producto.cantidad !== undefined && producto.cantidad <= 0) ? '#dc3545' : '#333'
        }}>{producto.nombre}</h4>
        
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: getResponsiveSize('11px', '12px', '13px'),
          marginBottom: '4px'
        }}>
          <span style={{ 
            fontWeight: 'bold',
            color: '#28a745',
            fontSize: getResponsiveSize('12px', '13px', '14px')
          }}>💰 ${formatearNumero(producto.precio)}</span>
          
          <span style={{ 
            color: (producto.cantidad !== null && producto.cantidad !== undefined) ? 
                  (producto.cantidad <= 5 ? (producto.cantidad <= 0 ? '#dc3545' : '#ffc107') : '#333') : 
                  '#28a745',
            fontWeight: '500'
          }}>📦 {(producto.cantidad !== null && producto.cantidad !== undefined) ? formatearNumero(producto.cantidad) : 'Stock no definido'}</span>
        </div>
        
        {/* Mostrar categoría con tema café */}
        {producto.categoria && (
          <span style={{ 
            display: 'inline-block',
            padding: '3px 8px',
            backgroundColor: '#e9ecef',
            color: '#495057',
            borderRadius: '10px',
            fontSize: getResponsiveSize('9px', '10px', '11px'),
            textAlign: 'center',
            fontWeight: '600',
            marginBottom: '4px',
            border: '1px solid #dee2e6'
          }}>
            📂 {producto.categoria}
          </span>
        )}        <div style={{ marginTop: 'auto' }}>
          <button 
            onClick={() => agregarProductoVenta(producto)}
            disabled={producto.cantidad !== null && producto.cantidad !== undefined && producto.cantidad <= 0}
            className="touch-friendly"
            style={{
              width: '100%',
              padding: getResponsivePadding('8px', '10px', '12px'),
              backgroundColor: (producto.cantidad === null || producto.cantidad === undefined || producto.cantidad > 0) ? '#007bff' : '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: (producto.cantidad === null || producto.cantidad === undefined || producto.cantidad > 0) ? 'pointer' : 'not-allowed',
              fontSize: getResponsiveSize('11px', '12px', '13px'),
              fontWeight: '600',
              opacity: (producto.cantidad !== null && producto.cantidad !== undefined && producto.cantidad <= 0) ? 0.7 : 1,
              transition: 'all 0.2s ease',
              boxShadow: (producto.cantidad === null || producto.cantidad === undefined || producto.cantidad > 0) ? '0 2px 4px rgba(0, 123, 255, 0.3)' : 'none',
              minHeight: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {(producto.cantidad === null || producto.cantidad === undefined || producto.cantidad > 0) ? '➕ Agregar' : '❌ Sin Stock'}
          </button>
        </div>
      </div>
    </div>
  );

  // Add this function to preload and validate image URL
  const validateAndLoadImage = async (url) => {
    if (!url) return DEFAULT_PROFILE_IMAGE;
    
    try {
      // Check if it's a valid URL
      new URL(url);
      
      // Try to load the image
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => resolve(DEFAULT_PROFILE_IMAGE);
        img.src = url;
      });
    } catch {
      return DEFAULT_PROFILE_IMAGE;
    }
  };

  // Add error handling function for images
  const handleImageError = (e) => {
    e.target.onerror = null; // Prevent infinite loop
    e.target.src = DEFAULT_PROFILE_IMAGE;
  };

  // Modificar handlePrintFactura
  const handlePrintFactura = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Imprimir Factura</title>
          <style>
            @media print {
              body {
                width: 80mm;
                margin: 0;
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          ${facturaRef.current.outerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleEmailClick = () => {
    setShowClienteForm(true);
  };

  const handleClienteSubmit = (cliente) => {
    setClienteData(cliente);
    setShowClienteForm(false);
    setShowEmailModal(true);
  };

  const handleSendEmail = async (email) => {
    try {
      await sendFacturaEmail({
        email,
        facturaId: ventaParaFacturar.id,
        total: ventaParaFacturar.total,
        fecha: ventaParaFacturar.fecha,
        productos: ventaParaFacturar.productos,
        vendedor: ventaParaFacturar.vendedorNombre,
        cliente: clienteData,
        metodoPago: ventaParaFacturar.metodoPago
      });
      
      showToast('Factura enviada exitosamente al correo: ' + email, 'success');
      setShowEmailModal(false);
      setClienteData(null);
    } catch (error) {
      console.error('Error al enviar email:', error);
      showToast(`Error al enviar email: ${error.message}`, 'error');
    }
  };

  // Add function to load pending orders from waiters
  const cargarVentasPendientes = async () => {
    try {
      // Solo cargar ventas pendientes si tenemos la sucursal del vendedor
      if (!userProfile.sucursal || userProfile.sucursal === 'No asignada') {
        console.log("⚠️ No se puede cargar ventas pendientes: sucursal del vendedor no válida");
        setVentasPendientes([]);
        return;
      }

      console.log("🔍 Cargando ventas pendientes para sucursal del vendedor:", userProfile.sucursal);
      
      // Cargar ventas pendientes de la colección "ventas_pendientes" (donde el mesero las coloca)
      const ventasPendientesQuery = query(
        collection(db, "ventas_pendientes"),
        where("estado", "==", "pendiente_cobro"),
        where("sucursal", "==", userProfile.sucursal)
      );
      
      const ventasSnapshot = await getDocs(ventasPendientesQuery);
      const ventasData = ventasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log("✅ Ventas pendientes cargadas para sucursal:", ventasData.length);
      setVentasPendientes(ventasData);
    } catch (error) {
      console.error("Error al cargar ventas pendientes:", error);
      // En caso de error de índice, cargar todas y filtrar en JavaScript
      if (error.message.includes('index')) {
        console.log("🔄 Intentando método alternativo sin índice...");
        try {
          const todasVentasQuery = query(
            collection(db, "ventas_pendientes"),
            where("estado", "==", "pendiente_cobro")
          );
          const ventasSnapshot = await getDocs(todasVentasQuery);
          const todasVentas = ventasSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Filtrar por sucursal en JavaScript
          const ventasFiltradas = todasVentas.filter(venta => 
            venta.sucursal === userProfile.sucursal
          );
          
          setVentasPendientes(ventasFiltradas);
          console.log("✅ Ventas pendientes cargadas (método alternativo):", ventasFiltradas.length);
        } catch (fallbackError) {
          console.error("Error en método alternativo:", fallbackError);
        }
      } else {
        // Si es otro tipo de error, intentar cargar sin filtro de sucursal
        console.log("🔄 Intentando cargar todas las ventas pendientes...");
        try {
          const todasVentasQuery = query(
            collection(db, "ventas_pendientes"),
            where("estado", "==", "pendiente_cobro")
          );
          const ventasSnapshot = await getDocs(todasVentasQuery);
          const todasVentas = ventasSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Filtrar por sucursal en JavaScript
          const ventasFiltradas = todasVentas.filter(venta => 
            venta.sucursal === userProfile.sucursal
          );
          
          setVentasPendientes(ventasFiltradas);
          console.log("✅ Ventas pendientes cargadas (sin filtro de sucursal):", ventasFiltradas.length);
        } catch (finalError) {
          console.error("Error final al cargar ventas pendientes:", finalError);
          setVentasPendientes([]);
        }
      }
    }
  };
  // Add function to process waiter order
  const procesarOrdenMesero = async (orden) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      
      // Buscar el objeto mesa correspondiente al número de mesa
      const mesaObj = mesas.find(m => m.numero === orden.mesa) || {
        id: `mesa-${orden.mesa}`,
        numero: orden.mesa,
        nombre: `Mesa ${orden.mesa}`,
        sucursal: orden.sucursal || userProfile.sucursal || 'No asignada'
      };
      
      // Convert to regular sale
      const ventaData = {
        productos: orden.productos,
        total: orden.total,
        efectivo: 0,
        cambio: 0,
        metodoPago: 'efectivo',
        fecha: Timestamp.now(),
        vendedorId: userData.id,
        vendedorNombre: userData.username,
        mesa: mesaObj, // Ahora asignamos el objeto mesa completo
        mesero: orden.mesero,
        sucursal: orden.sucursal || userProfile.sucursal || 'No asignada',
        ordenId: orden.id // Guardar el ID de la orden original para poder eliminarla después del pago
      };

      // Set up for regular sale process
      setVentaActual(ventaData);
      
      // Limpiar pago múltiple al procesar nueva orden
      setPagoMultiple({
        activo: false,
        pagos: [],
        totalPagado: 0,
        restante: 0
      });
      
      setSeccionActiva('productos');
      
      // Delete the order from ventas_pendientes since it's being processed
      await deleteDoc(doc(db, "ventas_pendientes", orden.id));
      
      // Reload pending orders and orders in process
      await cargarVentasPendientes();
      await cargarOrdenesEnProceso();
      
      showToast(`Orden de Mesa ${orden.mesa} agregada para cobro`, 'info');
    } catch (error) {
      console.error("Error al procesar orden:", error);
      showToast('Error al procesar la orden', 'error');
    }
  };

  // Añadir función para cargar órdenes en proceso de meseros
  const cargarOrdenesEnProceso = async () => {
    try {
      // Solo cargar órdenes si tenemos la sucursal del vendedor
      if (!userProfile.sucursal || userProfile.sucursal === 'No asignada') {
        console.log("⚠️ No se puede cargar órdenes en proceso: sucursal del vendedor no válida");
        setOrdenesEnProceso([]);
        return;
      }

      console.log("🔍 Cargando órdenes en proceso para sucursal del vendedor:", userProfile.sucursal);
      
      // Filtrar órdenes por sucursal del vendedor
      const ordenesQuery = query(
        collection(db, "ordenes"),
        where("estado", "in", ["en_proceso", "lista_para_cobrar"]),
        where("sucursal", "==", userProfile.sucursal)
      );
      
      const ordenesSnapshot = await getDocs(ordenesQuery);
      const ordenesData = ordenesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log("✅ Órdenes en proceso cargadas para sucursal:", ordenesData.length);
      setOrdenesEnProceso(ordenesData);
      
      // Limpiar el estado de órdenes impresas para órdenes que ya no están en proceso
      setOrdenesImpresas(prev => {
        const nuevasOrdenesIds = new Set(ordenesData.map(orden => orden.id));
        const ordenesImpresasActualizadas = new Set();
        
        // Solo mantener las órdenes impresas que aún están en proceso
        prev.forEach(ordenId => {
          if (nuevasOrdenesIds.has(ordenId)) {
            ordenesImpresasActualizadas.add(ordenId);
          }
        });
        
        return ordenesImpresasActualizadas;
      });
    } catch (error) {
      console.error("Error al cargar órdenes en proceso:", error);
      // En caso de error de índice, cargar todas y filtrar en JavaScript
      if (error.message.includes('index')) {
        console.log("🔄 Intentando método alternativo sin índice...");
        try {
          const ordenesQuery = query(
            collection(db, "ordenes"),
            where("estado", "in", ["en_proceso", "lista_para_cobrar"])
          );
          const ordenesSnapshot = await getDocs(ordenesQuery);
          const todasOrdenes = ordenesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Filtrar por sucursal en JavaScript
          const ordenesFiltradas = todasOrdenes.filter(orden => 
            orden.sucursal === userProfile.sucursal
          );
          
          setOrdenesEnProceso(ordenesFiltradas);
          
          // Limpiar el estado de órdenes impresas para órdenes que ya no están en proceso
          setOrdenesImpresas(prev => {
            const nuevasOrdenesIds = new Set(ordenesFiltradas.map(orden => orden.id));
            const ordenesImpresasActualizadas = new Set();
            
            // Solo mantener las órdenes impresas que aún están en proceso
            prev.forEach(ordenId => {
              if (nuevasOrdenesIds.has(ordenId)) {
                ordenesImpresasActualizadas.add(ordenId);
              }
            });
            
            return ordenesImpresasActualizadas;
          });
        } catch (fallbackError) {
          console.error("Error en método alternativo:", fallbackError);
        }
      } else {
        showToast('Error al cargar órdenes en proceso', 'error');
      }
    }
  };

  // Añadir función para imprimir orden
  const imprimirOrden = (orden) => {
    setOrdenSeleccionada(orden);
    setMostrarPreviewOrden(true);
  };

  // Función para marcar una orden como impresa
  const marcarOrdenComoImpresa = (ordenId) => {
    setOrdenesImpresas(prev => new Set(prev).add(ordenId));
    showToast('Orden impresa correctamente', 'success');
  };

  // Función para resetear el estado de impresión de una orden (para administradores)
  const resetearEstadoImpresion = (ordenId) => {
    setOrdenesImpresas(prev => {
      const nuevasOrdenesImpresas = new Set(prev);
      nuevasOrdenesImpresas.delete(ordenId);
      return nuevasOrdenesImpresas;
    });
    showToast('Estado de impresión reseteado', 'info');
  };

  // Función para imprimir la orden seleccionada
  const handlePrintOrden = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Orden - Mesa ${ordenSeleccionada.mesa}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              width: 80mm;
              margin: 0 auto;
            }
            h1, h2, h3, h4 {
              margin: 5px 0;
              text-align: center;
            }
            .header {
              text-align: center;
              margin-bottom: 15px;
            }
            .item {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
              border-bottom: 1px dashed #eee;
              padding-bottom: 5px;
            }
            .total {
              font-weight: bold;
              text-align: right;
              margin-top: 10px;
              font-size: 1.2em;
            }
            .notas {
              margin-top: 15px;
              padding: 10px;
              background-color: #f5f5f5;
              border-radius: 5px;
            }
            .footer {
              margin-top: 20px;
              text-align: center;
              font-size: 0.8em;
            }
            @media print {
              body {
                width: 80mm;
                margin: 0;
                padding: 5mm;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h3>ORDEN</h3>
            <h4>Mesa ${ordenSeleccionada.mesa}</h4>
            <p>Mesero: ${ordenSeleccionada.mesero}</p>
            <p>Fecha: ${ordenSeleccionada.fecha?.seconds ? new Date(ordenSeleccionada.fecha.seconds * 1000).toLocaleString() : ''}</p>
          </div>
          
          <div>
            ${ordenSeleccionada.productos.map(prod => `
              <div class="item">
                <span>${prod.cantidadOrden} x ${prod.nombre}</span>
                <span>$${formatearNumero(prod.precio * prod.cantidadOrden)}</span>
              </div>
            `).join('')}
          </div>
          
          <div class="total">
            Total: $${formatearNumero(ordenSeleccionada.total)}
          </div>
          
          ${ordenSeleccionada.notas ? `
            <div class="notas">
              <strong>Notas:</strong><br>
              ${ordenSeleccionada.notas}
            </div>
          ` : ''}
          
          <div class="footer">
            ${ordenSeleccionada.estado === 'en_proceso' ? 'EN PREPARACIÓN' : 'LISTA PARA COBRAR'}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    
    // Marcar la orden como impresa después de imprimir
    marcarOrdenComoImpresa(ordenSeleccionada.id);
    
    // Cerrar el preview
    setMostrarPreviewOrden(false);
    setOrdenSeleccionada(null);
  };

  return (
    <>
      {/* Toast notifications */}
      {toast.visible && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={closeToast} 
          duration={3000} 
        />
      )}

      <div 
        className="dashboard-container fade-in"
        style={{ 
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          fontSize: isMobile() ? '14px' : '16px',
          backgroundColor: '#FEFCF8',
          color: '#8B4513'
        }}
      >
        {/* Header móvil simplificado */}
        {isMobile() && (
          <div 
            className="vendedor-mobile-header"
            style={{
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
              borderBottom: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1003,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            <button
              onClick={() => setMenuMovilAbierto(!menuMovilAbierto)}
              style={{
                padding: '8px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '6px',
                fontSize: '16px',
                color: 'white',
                cursor: 'pointer',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              {menuMovilAbierto ? '✕' : '☰'}
            </button>
            
            <h1 style={{
              margin: 0,
              fontSize: '16px',
              color: 'white',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ☕ Vendedor
            </h1>
            
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {/* Indicador de carrito cuando está oculto */}
              {menuMovilAbierto && ventaActual.productos.length > 0 && (
                <div style={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '6px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  🛒 {ventaActual.productos.length}
                </div>
              )}
              
              <img
                src={userProfile.imagenURL || DEFAULT_PROFILE_IMAGE}
                alt="Perfil"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '2px solid white',
                  objectFit: 'cover'
                }}
              />
              <div style={{
                color: 'white',
                fontSize: '12px'
              }}>
                <div style={{ fontWeight: 'bold' }}>{userProfile.nombre}</div>
                <div style={{ opacity: 0.9 }}>{userProfile.sucursal}</div>
              </div>
            </div>
          </div>
        )}

        {/* Overlay para cerrar menú */}
        {isMobile() && menuMovilAbierto && (
          <div
            className="overlay"
            onClick={() => setMenuMovilAbierto(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: '280px', // Solo cubre el área fuera del menú
              width: 'calc(100vw - 280px)',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 998
            }}
          />
        )}

        {/* Menú móvil desplegable */}
        {isMobile() && menuMovilAbierto && (
          <>
            {/* Overlay para cerrar menú */}
            <div
              onClick={() => setMenuMovilAbierto(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 997
              }}
            />
            
            <div 
            className="mobile-menu open"
            style={{
              position: 'fixed',
              top: '60px', // Ajustado para el nuevo header
              left: 0,
              width: '250px',
              height: 'calc(100vh - 60px)', // Ajustado para el nuevo header
              backgroundColor: '#ffffff',
              borderRight: '1px solid #dee2e6',
              boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
              zIndex: 998,
              overflowY: 'auto',
              padding: '20px',
              transform: 'translateX(0)',
              transition: 'transform 0.3s ease'
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{
                margin: '0 0 15px 0',
                fontSize: '16px',
                color: '#333',
                borderBottom: '1px solid #dee2e6',
                paddingBottom: '8px'
              }}>
                📋 Navegación
              </h3>              {[
                { id: 'panel', icon: '📊', text: 'Panel Principal' },
                { id: 'productos', icon: '📦', text: 'Productos' },
                { id: 'ventas', icon: '📈', text: 'Historial de Ventas' },
                { 
                  id: 'ordenes_proceso', 
                  icon: '⏳', text: 'Órdenes en Proceso',
                  badge: ordenesEnProceso.length > 0 ? ordenesEnProceso.length : null,
                  onClick: () => cargarOrdenesEnProceso()
                },
                { 
                  id: 'ordenes_meseros', 
                  icon: '🍽️',
                  text: 'Órdenes de Meseros',
                  onClick: () => cargarVentasPendientes()
                }
              ].map(item => (
                <button
                  key={item.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSeccionActiva(item.id);
                    if (item.onClick) {
                      item.onClick();
                    }
                    setMenuMovilAbierto(false);
                  }}
                  className="touch-friendly"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    margin: '4px 0',
                    backgroundColor: seccionActiva === item.id ? '#007bff' : 'transparent',
                    color: seccionActiva === item.id ? 'white' : '#333',
                    border: seccionActiva === item.id ? 'none' : '1px solid #dee2e6',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: item.badge ? 'space-between' : 'flex-start',
                    gap: '10px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {item.icon && <span style={{ fontSize: '16px', minWidth: '20px', textAlign: 'center' }}>{item.icon}</span>}
                    <span>{item.text || item.label}</span>
                  </div>
                  {item.badge && (
                    <span style={{
                      backgroundColor: seccionActiva === item.id ? 'rgba(255,255,255,0.3)' : '#007bff',
                      color: 'white',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {item.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
            
            <div style={{
              borderTop: '1px solid #dee2e6',
              paddingTop: '15px',
              marginTop: '20px'
            }}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  localStorage.removeItem('user');
                  navigate('/');
                }}
                className="touch-friendly"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
              >
                🚪 Cerrar Sesión
              </button>
            </div>
          </div>
          </>
        )}

        {/* Barra lateral mejorada */}
        <div style={{ 
          width: isMobile() ? (isSmallMobile() ? '85%' : '80%') : '250px',
          height: '100vh',
          background: 'linear-gradient(180deg, #FEFCF8 0%, #F5F5DC 100%)',
          borderRight: '2px solid #D2B48C',
          position: 'fixed',
          left: isMobile() ? (menuMovilAbierto ? '0' : '-100%') : '0',
          top: 0,
          transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 1001,
          display: isMobile() ? 'none' : 'flex',
          flexDirection: 'column',
          boxShadow: isMobile() ? '2px 0 8px rgba(139, 69, 19, 0.15)' : 'none'
        }}>
          {/* Area fija superior - perfil mejorada */}
          <div style={{
            padding: getResponsivePadding('12px', '16px', '20px'),
            backgroundColor: 'transparent',
            borderBottom: '1px solid rgba(222, 226, 230, 0.5)'
          }}>
            {/* Perfil del vendedor mejorado */}
            <div style={{
              padding: getResponsivePadding('12px', '14px', '16px'),
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid rgba(222, 226, 230, 0.3)'
            }}>
              <div style={{ 
                width: getResponsiveSize('64px', '72px', '80px'),
                height: getResponsiveSize('64px', '72px', '80px'),
                margin: '0 auto 12px auto',
                position: 'relative'
              }}>
                <img 
                  src={userProfile.imagenURL}
                  alt={userProfile.nombre}
                  onError={handleImageError}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid #007bff',
                    backgroundColor: '#f8f9fa',
                    boxShadow: '0 2px 8px rgba(0,123,255,0.2)'
                  }}
                />
                {/* Status indicator */}
                <div style={{
                  position: 'absolute',
                  bottom: '2px',
                  right: '2px',
                  width: '16px',
                  height: '16px',
                  backgroundColor: '#28a745',
                  borderRadius: '50%',
                  border: '2px solid white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }}></div>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ 
                  margin: '0',
                  fontSize: getResponsiveSize('14px', '16px', '18px'),
                  color: '#2c3e50',
                  fontWeight: '600'
                }}>
                  {userProfile.nombre}
                </h3>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  background: '#007bff',
                  color: 'white',
                  borderRadius: '16px',
                  fontSize: getResponsiveSize('10px', '11px', '12px'),
                  marginTop: '6px',
                  fontWeight: '600',
                  boxShadow: '0 2px 6px rgba(0,123,255,0.3)',
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}>
                  {userProfile.rol}
                </span>
                {userProfile.sucursal && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: getResponsiveSize('11px', '12px', '13px'),
                    color: '#6c757d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    padding: '4px 8px',
                    backgroundColor: 'rgba(108, 117, 125, 0.1)',
                    borderRadius: '8px'
                  }}>
                    <span>📍</span>
                    <span>{userProfile.sucursal}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Area navegación con scroll si es necesario */}
          <nav style={{
            padding: '20px',
            flex: 1,
            overflowY: 'auto'
          }}>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li style={{ marginBottom: '10px' }}>
                <button 
                  onClick={() => setSeccionActiva('panel')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: seccionActiva === 'panel' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '44px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '16px' }}>📊</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>Panel Principal</span>
                </button>
              </li>
              <li style={{ marginBottom: '10px' }}>
                <button 
                  onClick={() => setSeccionActiva('productos')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: seccionActiva === 'productos' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '44px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '16px' }}>📦</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>Productos</span>
                </button>
              </li>
              <li style={{ marginBottom: '10px' }}>
                <button 
                  onClick={() => setSeccionActiva('ventas')}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: seccionActiva === 'ventas' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '44px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '16px' }}>📋</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>Historial de Ventas</span>
                </button>
              </li>
              {/* Nueva opción en el menú */}
              <li style={{ marginBottom: '10px' }}>
                <button 
                  onClick={() => {
                    setSeccionActiva('ordenes_proceso');
                    cargarOrdenesEnProceso(); // Recargar al cambiar a esta sección
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: seccionActiva === 'ordenes_proceso' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '44px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '16px' }}>⏳</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>Órdenes en Proceso</span>
                  {ordenesEnProceso.length > 0 && (
                    <span style={{
                      backgroundColor: 'white',
                      color: '#6c757d',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      {ordenesEnProceso.length}
                    </span>
                  )}
                </button>
              </li>
              <li style={{ marginBottom: '10px' }}>
                <button 
                  onClick={() => {
                    setSeccionActiva('ordenes_meseros');
                    cargarVentasPendientes(); // Recargar órdenes al cambiar a esta sección
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: seccionActiva === 'ordenes_meseros' ? '#007bff' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '44px'
                  }}
                >
                  <span style={{ minWidth: '20px', textAlign: 'center', fontSize: '16px' }}>🍽️</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>Órdenes de Meseros</span>
                </button>
              </li>
            </ul>
          </nav>

          {/* Botón de cerrar sesión con tema café */}
          <div style={{
            padding: '20px',
            borderTop: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa'
          }}>
            <button
              onClick={() => {
                localStorage.removeItem('user');
                navigate('/');
              }}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
            >
              <span>🚪</span>
              Cerrar Sesión
            </button>
          </div>
        </div>

        {/* Contenido principal responsive */}
        <div 
          className={`dashboard-container ${isMobile() ? 'vendedor-content' : ''}`}
          style={{ 
            flex: 1,
            marginLeft: isMobile() ? '0' : '250px',
            padding: getResponsivePadding('8px', '20px', '40px'),
            paddingTop: isMobile() ? '0px' : '20px', // Reducido para evitar espacio excesivo
            height: '100vh',
            overflowY: 'auto',
            paddingBottom: isMobile() ? '60px' : '40px',
            backgroundColor: '#ffffff',
            color: '#333'
          }}
        >
          <div 
            className="fade-in"
            style={{ 
              maxWidth: '1400px',
              margin: '0 auto',
              width: '100%',
              paddingLeft: getResponsivePadding('4px', '10px', '20px'),
              paddingRight: getResponsivePadding('4px', '10px', '20px')
            }}
          >
            {renderSeccion()}
          </div>
        </div>
      </div>

      {/* Reemplazar el div oculto de la factura con este modal */}
      {mostrarPreviewFactura && ventaParaFacturar && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              padding: '10px',
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              borderBottom: '1px solid #dee2e6',
              marginBottom: '20px'
            }}>
              <button
                onClick={handleEmailClick}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                📧 Enviar por Email
              </button>
              <button
                onClick={handlePrintFactura}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🖨️ Imprimir
              </button>
              <button
                onClick={() => setMostrarPreviewFactura(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ✖ Cerrar
              </button>
            </div>
            
            <div ref={facturaRef}>
              <Factura
                venta={ventaParaFacturar}
                fecha={ventaParaFacturar.fecha}
                tipo="thermal"
                cliente={clienteData}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de datos del cliente */}
      {showClienteForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2100
        }}>
          <ClienteForm
            onSubmit={handleClienteSubmit}
            onCancel={() => setShowClienteForm(false)}
          />
        </div>
      )}

      <EmailModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setClienteData(null);
        }}
        onSend={handleSendEmail}
        initialEmail={clienteData?.email || ''}
      />

      {/* Modal para previsualización de orden */}
      {mostrarPreviewOrden && ordenSeleccionada && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              padding: '10px',
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              borderBottom: '1px solid #dee2e6',
              marginBottom: '20px'
            }}>
              <button
                onClick={handlePrintOrden}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🖨️ Imprimir
              </button>
              <button
                onClick={() => setMostrarPreviewOrden(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ✖ Cerrar
              </button>
            </div>
            
            <div ref={ordenRef} style={{
              fontFamily: 'Arial, sans-serif',
              padding: '10px',
              maxWidth: '300px',
              margin: '0 auto'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: '5px 0' }}>ORDEN</h3>
                <h4 style={{ margin: '5px 0' }}>Mesa {ordenSeleccionada.mesa}</h4>
                <p style={{ margin: '5px 0' }}>Mesero: {ordenSeleccionada.mesero}</p>
                <p style={{ margin: '5px 0' }}>
                  Fecha: {ordenSeleccionada.fecha?.seconds ? new Date(ordenSeleccionada.fecha.seconds * 1000).toLocaleString() : ''}
                </p>
              </div>
              
              <div>
                {ordenSeleccionada.productos.map((prod, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '5px',
                    borderBottom: '1px dashed #eee',
                    paddingBottom: '5px'
                  }}>
                    <span>{prod.cantidadOrden} x {prod.nombre}</span>
                    <span>${formatearNumero(prod.precio * prod.cantidadOrden)}</span>
                  </div>
                ))}
              </div>
              
              <div style={{ 
                fontWeight: 'bold', 
                textAlign: 'right', 
                marginTop: '10px',
                fontSize: '1.2em'
              }}>
                Total: ${formatearNumero(ordenSeleccionada.total)}
              </div>
              
              {ordenSeleccionada.notas && (
                <div style={{
                  marginTop: '15px',
                  padding: '10px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '5px'
                }}>
                  <strong>Notas:</strong><br />
                  {ordenSeleccionada.notas}
                </div>
              )}
              
              <div style={{
                marginTop: '20px',
                textAlign: 'center',
                fontSize: '0.8em'
              }}>
                {ordenSeleccionada.estado === 'en_proceso' ? 'EN PREPARACIÓN' : 'LISTA PARA COBRAR'}
              </div>
            </div>
          </div>
        </div>  
      )}

      {/* Modal de Pago Múltiple */}
      {mostrarPagoMultiple && (
        <PagoMultiple
          total={ventaActual.total}
          pagos={pagoMultiple.pagos}
          totalPagado={pagoMultiple.totalPagado}
          restante={pagoMultiple.restante}
          onAgregarPago={agregarPago}
          onEliminarPago={eliminarPago}
          onClose={() => setMostrarPagoMultiple(false)}
          formatearNumero={formatearNumero}
        />
      )}

      {/* Modal de División de Cuenta */}
      {mostrarDivisionCuenta && (
        <DivisionCuenta
          productosVenta={ventaActual.productos}
          totalVenta={ventaActual.total}
          onConfirmarDivision={confirmarDivisionCuenta}
          onCancelar={() => setMostrarDivisionCuenta(false)}
          formatearNumero={formatearNumero}
        />
      )}
    </>
  );
}

export default DashboardVendedor;
